/**
 * AdminApp.jsx — Iter 57. Full Admin Console shell + all 11 sub-pages.
 * Dark studio theme, sidebar navigation, minimal spacing.
 *
 * Sections: Overview · Users · Vendors · Agencies · Catalog · Coupons ·
 * Programs · Data Backup · Payments · Communications · Audit · Module Access ·
 * Feature Flags · System · Admins
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Routes, Route, Link, useLocation, useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import api from '@/api';
import { toast } from 'sonner';
import {
  ShieldCheck, Gauge, Users, Buildings, Storefront, Ticket, Presentation,
  CurrencyInr, PaperPlaneTilt, ClockCounterClockwise, Flag, Heartbeat, UserPlus,
  SignOut, MagnifyingGlass, Plus, TrashSimple, ArrowClockwise, UserCircle, Check, X, Gear, Sun, Moon,
  UsersThree, PencilSimple, Trash, Warning, SpinnerGap, DownloadSimple, Archive,
  ToggleLeft, LockSimple, LockSimpleOpen, ArrowCounterClockwise, CaretRight, CaretDown,
  Bank, ChatCircleDots, FileText, Package, EnvelopeSimple, Receipt, Infinity as InfinityIcon,
} from '@phosphor-icons/react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import ServiceChat from '@/components/ServiceChat';
import { formatPhone } from '@/utils/phone';
import BroadcastPage from './BroadcastPage';
import FounderboardCoinIcon from '@/components/FounderboardCoinIcon';
import './admin-theme.css';

// Sidebar sections, in display order. `group: null` renders without a heading;
// Settings lives in the pinned sidebar footer instead of the scrolling list.
const NAV_GROUPS = [null, 'People', 'Marketplace', 'Finance', 'Programs & outreach', 'Platform'];
const NAV = [
  { path: '/admin',              label: 'Overview',       icon: Gauge,                    testid: 'nav-overview',    exact: true, group: null },
  { path: '/admin/users',        label: 'Users',          icon: Users,                    testid: 'nav-users',       group: 'People' },
  { path: '/admin/manage-users', label: 'Manage Users',   icon: UsersThree,               testid: 'nav-manage-users', group: 'People' },
  { path: '/admin/admins',       label: 'Admin Access',   icon: UserPlus,                 testid: 'nav-admins',      group: 'People' },
  { path: '/admin/vendors',      label: 'Vendors',        icon: Storefront,               testid: 'nav-vendors',     group: 'Marketplace' },
  { path: '/admin/agencies',     label: 'Agencies',       icon: Buildings,                testid: 'nav-agencies',    group: 'Marketplace' },
  { path: '/admin/catalog',      label: 'Catalog',        icon: Package,                  testid: 'nav-catalog',     group: 'Marketplace' },
  { path: '/admin/vault-docs',   label: 'Vault Documents', icon: FileText,                testid: 'nav-vault-docs',  group: 'Marketplace' },
  { path: '/admin/credit',       label: 'Credit Desk',    icon: Bank,                     testid: 'nav-credit',      group: 'Marketplace' },
  { path: '/admin/payments',     label: 'Payments',       icon: CurrencyInr,              testid: 'nav-payments',    group: 'Finance' },
  { path: '/admin/coupons',      label: 'Coupons',        icon: Ticket,                   testid: 'nav-coupons',     group: 'Finance' },
  { path: '/admin/vc-billing',   label: 'Incubator/VC Billing', icon: Receipt,            testid: 'nav-vc-billing',  group: 'Finance' },
  { path: '/admin/coins',        label: 'Founderboard Coins', icon: FounderboardCoinIcon, testid: 'nav-coins',       group: 'Finance' },
  { path: '/admin/programs',     label: 'Programs',       icon: Presentation,             testid: 'nav-programs',    group: 'Programs & outreach' },
  { path: '/admin/comms',        label: 'Broadcast',      icon: PaperPlaneTilt,           testid: 'nav-comms',       group: 'Programs & outreach' },
  { path: '/admin/email-reminders', label: 'Email Reminders', icon: EnvelopeSimple,       testid: 'nav-email-reminders', group: 'Programs & outreach' },
  { path: '/admin/modules',      label: 'Module Access',  icon: ToggleLeft,               testid: 'nav-modules',     group: 'Platform' },
  { path: '/admin/flags',        label: 'Feature Flags',  icon: Flag,                     testid: 'nav-flags',       group: 'Platform' },
  { path: '/admin/system',       label: 'System Health',  icon: Heartbeat,                testid: 'nav-system',      group: 'Platform' },
  { path: '/admin/backup',       label: 'Data Backup',    icon: Archive,                  testid: 'nav-backup',      group: 'Platform' },
  { path: '/admin/audit',        label: 'Audit Log',      icon: ClockCounterClockwise,    testid: 'nav-audit',       group: 'Platform' },
];
const SETTINGS_NAV = { path: '/admin/settings', label: 'Settings', icon: Gear, testid: 'nav-settings', group: 'Account' };

const isNavActive = (n, pathname) => (n.exact ? pathname === n.path : pathname === n.path || pathname.startsWith(n.path + '/'));

const NavLink = ({ n, active }) => {
  const I = n.icon;
  return (
    <Link to={n.path} data-testid={n.testid} aria-current={active ? 'page' : undefined}
      className={`flex items-center gap-2.5 px-3 py-1.5 text-[13px] rounded-lg transition-colors ${active ? 'bg-emerald-500/10 text-emerald-300 font-semibold' : 'text-slate-400 hover:text-slate-100 hover:bg-slate-800/50'}`}>
      <I size={14} weight={active ? 'fill' : 'bold'} className="flex-shrink-0" /> {n.label}
    </Link>
  );
};

const AdminApp = ({ user }) => {
  const location = useLocation();
  const nav = useNavigate();
  const [theme, setTheme] = useState(() => localStorage.getItem('admin_theme') || 'dark');
  useEffect(() => { localStorage.setItem('admin_theme', theme); }, [theme]);
  const toggleTheme = () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'));
  const logout = () => {
    localStorage.removeItem('token'); localStorage.removeItem('user');
    window.location.assign('/admin/login');
  };

  return (
    // Fixed-height shell: the sidebar never scrolls away — only its nav list scrolls
    // (if the window is short) and page content scrolls inside <main>.
    <div className="h-screen overflow-hidden bg-[#0a0e14] text-slate-100 flex" data-testid="admin-app" data-admin-theme={theme}>
      <aside className="w-56 h-full flex-shrink-0 border-r border-slate-800 flex flex-col bg-[#0d1219]">
        <div className="px-5 py-4 border-b border-slate-800 flex items-center gap-2 flex-shrink-0">
          <ShieldCheck size={18} weight="fill" className="text-emerald-400" />
          <div>
            <div className="text-[9px] uppercase tracking-[0.24em] text-emerald-400 font-bold">Founderboard</div>
            <div className="text-sm font-heading font-bold leading-none">Admin</div>
          </div>
        </div>
        <nav className="flex-1 min-h-0 px-2 py-2 overflow-y-auto" data-testid="admin-nav">
          {NAV_GROUPS.map((g) => (
            <div key={g || 'top'} className={g ? 'mt-3' : ''}>
              {g && <div className="px-3 pb-1 text-[9px] uppercase tracking-[0.18em] font-bold text-slate-600">{g}</div>}
              <div className="space-y-0.5">
                {NAV.filter((n) => n.group === g).map((n) => (
                  <NavLink key={n.path} n={n} active={isNavActive(n, location.pathname)} />
                ))}
              </div>
            </div>
          ))}
        </nav>
        {/* Pinned footer — settings, theme, account and sign-out are always visible. */}
        <div className="flex-shrink-0 border-t border-slate-800 px-2 pt-2 pb-3 space-y-1">
          <NavLink n={SETTINGS_NAV} active={isNavActive(SETTINGS_NAV, location.pathname)} />
          <div className="grid grid-cols-2 gap-1">
            <button
              onClick={toggleTheme}
              data-testid="admin-theme-toggle"
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              className="flex items-center justify-center gap-1.5 px-2 py-1.5 text-[11px] text-slate-500 hover:text-slate-200 hover:bg-slate-800/50 rounded-lg"
            >
              {theme === 'dark' ? <Sun size={12} weight="bold" /> : <Moon size={12} weight="bold" />}
              {theme === 'dark' ? 'Light' : 'Dark'}
            </button>
            <button onClick={() => nav('/startup')} className="flex items-center justify-center gap-1.5 px-2 py-1.5 text-[11px] text-slate-500 hover:text-slate-200 hover:bg-slate-800/50 rounded-lg" data-testid="admin-back-to-app">
              <ArrowClockwise size={12} weight="bold" /> Back to app
            </button>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-800">
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium truncate text-slate-200" title={user?.email}>{user?.email}</div>
              <div className="text-[10px] text-emerald-400">{user?.is_super_admin ? 'Super admin' : (user?.studio_role || 'admin')}</div>
            </div>
            <button onClick={logout} data-testid="admin-logout" title="Sign out" aria-label="Sign out"
              className="flex-shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-red-400 hover:text-red-300">
              <SignOut size={13} weight="bold" /> Sign out
            </button>
          </div>
        </div>
      </aside>
      <main className="flex-1 min-w-0 h-full overflow-y-auto">
        <Routes>
          <Route path="/"          element={<OverviewPage />} />
          <Route path="/users"     element={<UsersPage />} />
          <Route path="/manage-users" element={<ManageUsersPage me={user} />} />
          <Route path="/vendors"   element={<VendorsSection />} />
          <Route path="/agencies"  element={<AgenciesPage />} />
          <Route path="/catalog"   element={<CatalogPage />} />
          <Route path="/vault-docs" element={<VaultDocRequestsPage />} />
          <Route path="/coupons"   element={<CouponsPage />} />
          <Route path="/credit"    element={<CreditDeskPage />} />
          <Route path="/programs"  element={<ProgramsPage />} />
          <Route path="/backup"    element={<BackupPage me={user} />} />
          <Route path="/payments"  element={<PaymentsPage />} />
          <Route path="/vc-billing" element={<VcBillingPage />} />
          <Route path="/coins"     element={<CoinsAdminPage />} />
          <Route path="/email-reminders" element={<EmailRemindersPage />} />
          <Route path="/comms"     element={<BroadcastPage />} />
          <Route path="/audit"     element={<AuditPage />} />
          <Route path="/modules"   element={<ModuleAccessPage />} />
          <Route path="/flags"     element={<FlagsPage />} />
          <Route path="/system"    element={<SystemPage />} />
          <Route path="/admins"    element={<AdminsPage />} />
          <Route path="/settings"  element={<SettingsPage user={user} />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </main>
    </div>
  );
};

// ─────────────── shared UI ───────────────
const Page = ({ title, subtitle, actions, tabs, children, testid }) => {
  // Kicker shows the sidebar section this page belongs to (e.g. "Admin · Marketplace").
  const { pathname } = useLocation();
  const section = [...NAV, SETTINGS_NAV].find((n) => isNavActive(n, pathname))?.group;
  return (
    <div className="p-6 lg:p-8 max-w-[1600px]" data-testid={testid}>
      <div className={`mb-6 border-b border-slate-800 ${tabs ? '' : 'pb-5'}`}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-1">
              <span className="text-slate-500">Admin</span>
              {section && <><span className="text-slate-600 mx-1.5">/</span><span className="text-emerald-400">{section}</span></>}
            </div>
            <h1 className="text-2xl font-heading font-bold" style={{ letterSpacing: '-0.02em' }}>{title}</h1>
            {subtitle && <p className="text-sm text-slate-400 mt-1 max-w-3xl">{subtitle}</p>}
          </div>
          {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
        </div>
        {tabs && <div className="mt-4 -mb-px">{tabs}</div>}
      </div>
      {children}
    </div>
  );
};

const StatCard = ({ label, value, sub, tone = 'emerald' }) => {
  const toneCls = { emerald: 'text-emerald-400', amber: 'text-amber-400', red: 'text-red-400', slate: 'text-slate-300' }[tone] || 'text-slate-300';
  return (
    <div className="bg-[#111827] border border-slate-800 rounded-xl p-4">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">{label}</div>
      <div className={`text-2xl font-heading font-bold ${toneCls}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
};

// `renderExpanded(row, i)` — return a node to hang a full-width drawer under that
// row, or null/undefined to leave it collapsed.
const Table = ({ cols, rows, testid, empty = 'No data.', renderExpanded }) => (
  <div className="bg-[#111827] border border-slate-800 rounded-xl overflow-x-auto" data-testid={testid}>
    <table className="w-full min-w-[800px] text-sm">
      <thead className="bg-slate-800/40 text-[10px] uppercase tracking-wider text-slate-500">
        <tr>{cols.map((c) => <th key={c.key} className="text-left px-4 py-2.5 font-semibold">{c.label}</th>)}</tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan={cols.length} className="text-center text-slate-500 py-8">{empty}</td></tr>
        ) : rows.map((r, i) => {
          const drawer = renderExpanded?.(r, i);
          return (
            <React.Fragment key={i}>
              <tr className="border-t border-slate-800 hover:bg-slate-800/30">
                {cols.map((c) => <td key={c.key} className="px-4 py-2.5 text-slate-200">{c.render ? c.render(r) : (r[c.key] ?? '—')}</td>)}
              </tr>
              {drawer && (
                <tr className="border-t border-slate-800 bg-[#0a0e14]">
                  <td colSpan={cols.length} className="px-4 py-4">{drawer}</td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  </div>
);

// ─────────────── OVERVIEW ───────────────
const OverviewPage = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get('/admin/dashboard').then((r) => setData(r.data)).catch((e) => toast.error(e.response?.data?.detail || 'Load failed')); }, []);
  if (!data) return <Page title="Overview" testid="overview-page"><div className="text-sm text-slate-500">Loading…</div></Page>;
  const PIE_COLORS = ['#10b981', '#f59e0b', '#3b82f6', '#a855f7', '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#64748b'];
  return (
    <Page title="Overview" subtitle="Real-time snapshot of the whole platform." testid="overview-page">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="Total users" value={data.kpis.users_total.toLocaleString('en-IN')} sub={data.kpis.mom_growth_pct != null ? `${data.kpis.mom_growth_pct > 0 ? '+' : ''}${data.kpis.mom_growth_pct}% MoM` : `${data.kpis.users_last_30d} in last 30d`} />
        <StatCard label="Vendors approved" value={data.kpis.vendors_approved} tone="amber" />
        <StatCard label="Agencies active" value={data.kpis.agencies_active} tone="slate" />
        <StatCard label="GMV paid (₹)" value={data.kpis.gmv_paid.toLocaleString('en-IN')} sub={`Pending ₹${data.kpis.gmv_pending.toLocaleString('en-IN')}`} tone="emerald" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-[#111827] border border-slate-800 rounded-xl p-4">
          <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">Signups per week</div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.signups_series}>
              <XAxis dataKey="week" stroke="#475569" fontSize={10} /><YAxis stroke="#475569" fontSize={10} />
              <Tooltip contentStyle={{ background: '#0d1219', border: '1px solid #334155', fontSize: 12 }} />
              <Line type="monotone" dataKey="signups" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-[#111827] border border-slate-800 rounded-xl p-4">
          <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">Startup sectors</div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={data.sectors} dataKey="count" nameKey="name" outerRadius={80} innerRadius={40} paddingAngle={2}>
                {data.sectors.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: '#0d1219', border: '1px solid #334155', fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-[#111827] border border-slate-800 rounded-xl p-4">
          <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">Vendor funnel</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={[{ label: 'Applied', v: data.funnel.applied }, { label: 'Approved', v: data.funnel.approved }, { label: 'Active', v: data.funnel.active }]}>
              <XAxis dataKey="label" stroke="#475569" fontSize={11} /><YAxis stroke="#475569" fontSize={10} />
              <Tooltip contentStyle={{ background: '#0d1219', border: '1px solid #334155', fontSize: 12 }} />
              <Bar dataKey="v" fill="#f59e0b" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-[#111827] border border-slate-800 rounded-xl p-4">
          <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">Needs attention</div>
          <div className="space-y-2 text-sm">
            <AttentionRow label="Pending vendor applications" value={data.attention.pending_vendors} link="/admin/vendors" />
            <AttentionRow label="Pending agency applications" value={data.attention.pending_agencies} link="/admin/agencies" />
            <AttentionRow label="Pending admin signup requests" value={data.attention.pending_admins} link="/admin/admins" />
            <AttentionRow label="Pending catalog suggestions" value={data.attention.pending_catalog_suggestions} link="/admin/catalog" />
            <AttentionRow label="Pending vault document requests" value={data.attention.pending_vault_doc_requests ?? 0} link="/admin/vault-docs" />
          </div>
        </div>
      </div>
    </Page>
  );
};

const AttentionRow = ({ label, value, link }) => (
  <Link to={link} className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-slate-800/40" data-testid={`attention-${label.replace(/\s+/g, '-').toLowerCase()}`}>
    <span className="text-slate-300">{label}</span>
    <span className={`inline-flex items-center px-2 py-0.5 rounded font-mono text-xs ${value > 0 ? 'bg-amber-500/15 text-amber-400' : 'bg-slate-800 text-slate-500'}`}>{value}</span>
  </Link>
);

// ─────────────── USERS + IMPERSONATION ───────────────
// `org_name` / `org_type` come from /admin/users — the startup, fund, incubator,
// vendor or agency workspace this account belongs to (as owner or team member).
const ORG_TONE = {
  startup:   'bg-emerald-500/15 text-emerald-400',
  vc:        'bg-blue-500/15 text-blue-400',
  incubator: 'bg-amber-500/15 text-amber-400',
  vendor:    'bg-purple-500/15 text-purple-400',
  agency:    'bg-slate-700 text-slate-300',
};

const OrgCell = ({ user }) => {
  if (!user.org_name) return <span className="text-slate-600 text-xs">—</span>;
  return (
    <div className="flex items-center gap-1.5 min-w-0">
      <span className="text-xs truncate max-w-[180px]" title={user.org_name}>{user.org_name}</span>
      <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0 ${ORG_TONE[user.org_type] || 'bg-slate-800 text-slate-400'}`}>{user.org_type}</span>
    </div>
  );
};

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState(''); const [role, setRole] = useState('');
  // The filters the table is actually showing — search applies on Enter, so the
  // export follows these rather than whatever is half-typed in the box.
  const [applied, setApplied] = useState({ q: '', role: '' });
  const [exporting, setExporting] = useState(false);
  const load = async () => {
    const r = await api.get('/admin/users', { params: { q: q || undefined, role: role || undefined, limit: 200 } });
    setUsers(r.data.users || []);
    setApplied({ q, role });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [role]);
  const filtered = !!(applied.q || applied.role);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const r = await api.get('/admin/users/excel', {
        params: { q: applied.q || undefined, role: applied.role || undefined },
        responseType: 'blob',
      });
      const name = (r.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || 'founderboard-users.xlsx';
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
      const n = r.headers['x-row-count'];
      toast.success(`Exported ${n ?? ''} ${filtered ? 'filtered ' : ''}user${n === '1' ? '' : 's'}`.replace(/\s+/g, ' '));
    } catch (e) { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  const doAction = async (u, action) => {
    if (!window.confirm(`${action} for ${u.email}?`)) return;
    try {
      if (action === 'impersonate') {
        const r = await api.post(`/admin/users/${u.id}/impersonate`, {});
        localStorage.setItem('token', r.data.token); localStorage.setItem('user', JSON.stringify(r.data.user));
        // Hard navigation, not nav('/'): the impersonated user has no admin flags, so an
        // in-place setUser() would make the surrounding /admin/* guard bounce to /admin/login
        // before the redirect lands, and the currency/module providers would keep the admin's
        // state. Reloading at / re-runs /auth/me and lets getDefaultRoute pick the shell.
        window.location.href = '/';
      } else {
        await api.post(`/admin/users/${u.id}/${action}`, {});
        toast.success('Done'); load();
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <Page title="Users" subtitle="Search, deactivate, reset password, or impersonate." testid="users-page"
      actions={<>
        <div className="relative"><MagnifyingGlass size={12} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()}
            data-testid="users-search" className="bg-[#111827] border border-slate-800 rounded-lg pl-7 pr-3 py-1.5 text-xs w-52 focus:outline-none focus:border-emerald-500" /></div>
        <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="users-role-filter"
          className="bg-[#111827] border border-slate-800 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-emerald-500">
          <option value="">All roles</option>
          {['startup', 'vc', 'admin', 'vendor'].map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <button onClick={exportExcel} disabled={exporting} data-testid="users-export"
          title={filtered ? 'Download only the users matching the current filters' : 'Download every user'}
          className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50">
          {exporting ? <SpinnerGap size={12} weight="bold" className="animate-spin" /> : <DownloadSimple size={12} weight="bold" />}
          {exporting ? 'Preparing…' : (filtered ? 'Export filtered (.xlsx)' : 'Export all (.xlsx)')}
        </button>
      </>}>
      {filtered && (
        <div className="text-[11px] text-slate-500 mb-2" data-testid="users-filter-note">
          Filtered by {[applied.role && `role: ${applied.role}`, applied.q && `search: “${applied.q}”`].filter(Boolean).join(' · ')}
          {' '}— the export includes only these users{users.length >= 200 ? ' (all matches, not just the first 200 shown)' : ''}.
        </div>
      )}
      <Table testid="users-table" cols={[
        { key: 'email', label: 'Email', render: (r) => <span className="font-mono text-xs">{r.email}</span> },
        { key: 'full_name', label: 'Name' },
        { key: 'phone', label: 'Phone', render: (r) => (r.phone ? (
          <a href={`tel:${r.phone}`} data-testid={`user-phone-${r.id}`}
            title={r.phone_source && r.phone_source !== 'account' ? `From their ${r.phone_source} profile` : undefined}
            className="text-xs font-mono text-slate-200 hover:underline whitespace-nowrap">
            {formatPhone(r.phone)}
            {r.phone_source && r.phone_source !== 'account' && <span className="ml-1 text-[9px] uppercase text-slate-500">{r.phone_source}</span>}
          </a>
        ) : <span className="text-slate-600 text-xs">—</span>) },
        { key: 'org_name', label: 'Organisation', render: (r) => <OrgCell user={r} /> },
        { key: 'role', label: 'Role', render: (r) => <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-slate-800">{r.role}</span> },
        { key: 'onboarding_complete', label: 'Onboarded', render: (r) => r.onboarding_complete ? <Check size={12} className="text-emerald-400" /> : <X size={12} className="text-slate-600" /> },
        { key: 'deactivated', label: 'Status', render: (r) => r.deactivated ? <span className="text-red-400 text-xs">deactivated</span> : <span className="text-emerald-400 text-xs">active</span> },
        { key: 'actions', label: '', render: (r) => (
          <div className="flex gap-1">
            <button onClick={() => doAction(r, 'impersonate')} data-testid={`user-impersonate-${r.id}`} className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25"><UserCircle size={10} weight="bold" className="inline mr-0.5" />Impersonate</button>
            <button onClick={() => doAction(r, 'reset-password')} data-testid={`user-reset-${r.id}`} className="text-[10px] px-2 py-0.5 rounded bg-slate-700 hover:bg-slate-600">Reset password</button>
            {r.deactivated ? (
              <button onClick={() => doAction(r, 'reactivate')} data-testid={`user-reactivate-${r.id}`} className="text-[10px] px-2 py-0.5 rounded bg-slate-700 hover:bg-slate-600">Reactivate</button>
            ) : (
              <button onClick={() => doAction(r, 'deactivate')} data-testid={`user-deactivate-${r.id}`} className="text-[10px] px-2 py-0.5 rounded bg-red-500/15 text-red-400 hover:bg-red-500/25">Deactivate</button>
            )}
          </div>
        )},
      ]} rows={users} empty="No matching users." />
    </Page>
  );
};

// ─────────────── MANAGE USERS (edit + cascade delete) ───────────────
const ROLE_OPTIONS = ['startup', 'vc', 'vendor', 'admin', 'owner'];
const STUDIO_ROLE_OPTIONS = ['member', 'admin', 'owner'];

const Modal = ({ title, subtitle, onClose, children, wide }) => (
  <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 p-4 py-10 overflow-y-auto" onMouseDown={onClose}>
    <div onMouseDown={(e) => e.stopPropagation()}
      className={`w-full ${wide ? 'max-w-2xl' : 'max-w-lg'} bg-[#111827] border border-slate-800 rounded-2xl p-5 shadow-2xl`}>
      <div className="flex items-start justify-between mb-4 gap-4">
        <div>
          <h2 className="text-base font-heading font-bold">{title}</h2>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-200 shrink-0" data-testid="modal-close"><X size={14} weight="bold" /></button>
      </div>
      {children}
    </div>
  </div>
);

const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">{label}</label>
    {children}
    {hint && <p className="text-[10px] text-slate-600 mt-1">{hint}</p>}
  </div>
);

const inputCls = 'w-full bg-[#0a0e14] border border-slate-800 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500 disabled:opacity-40';

const Toggle = ({ label, checked, onChange, disabled, testid }) => (
  <label className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-[#0a0e14] border border-slate-800 ${disabled ? 'opacity-40' : 'cursor-pointer'}`}>
    <span className="text-xs text-slate-300">{label}</span>
    <input type="checkbox" checked={checked} disabled={disabled} data-testid={testid}
      onChange={(e) => onChange(e.target.checked)} className="accent-emerald-500" />
  </label>
);

const ManageUsersPage = ({ me }) => {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState('');
  const [role, setRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/users', { params: { q: q || undefined, role: role || undefined, limit: 500 } });
      setUsers(r.data.users || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load users');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [role]);

  const onSaved = (updated) => {
    setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
    setEditing(null);
  };
  const onDeleted = (id) => {
    setUsers((prev) => prev.filter((u) => u.id !== id));
    setDeleting(null);
  };

  return (
    <Page title="Manage Users" testid="manage-users-page"
      subtitle={`${users.length} account${users.length === 1 ? '' : 's'}. Edit details, or permanently delete an account together with every record tied to it.`}
      actions={<>
        <div className="relative">
          <MagnifyingGlass size={12} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input placeholder="Search name or email…" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()} data-testid="manage-users-search"
            className="bg-[#111827] border border-slate-800 rounded-lg pl-7 pr-3 py-1.5 text-xs w-56 focus:outline-none focus:border-emerald-500" />
        </div>
        <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="manage-users-role-filter"
          className="bg-[#111827] border border-slate-800 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-emerald-500">
          <option value="">All roles</option>
          {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <button onClick={load} data-testid="manage-users-refresh"
          className="inline-flex items-center gap-1 border border-slate-700 hover:border-slate-500 text-xs px-3 py-1.5 rounded-lg">
          <ArrowClockwise size={11} weight="bold" /> Refresh
        </button>
      </>}>

      {!me?.is_super_admin && (
        <div className="mb-4 flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 text-xs text-amber-300" data-testid="manage-users-perm-note">
          <Warning size={14} weight="bold" className="mt-0.5 shrink-0" />
          <span>You can edit profile details, but deleting an account and changing roles or admin grants is restricted to super admins.</span>
        </div>
      )}

      {loading ? <div className="text-sm text-slate-500">Loading…</div> : (
        <Table testid="manage-users-table" empty="No matching users." rows={users} cols={[
          { key: 'email', label: 'Email', render: (r) => (
            <div>
              <div className="font-mono text-xs">{r.email}</div>
              <div className="text-[10px] text-slate-600 font-mono">{r.id?.slice(0, 8)}</div>
            </div>
          ) },
          { key: 'full_name', label: 'Name' },
          { key: 'org_name', label: 'Organisation', render: (r) => <OrgCell user={r} /> },
          { key: 'role', label: 'Role', render: (r) => <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-slate-800">{r.role}</span> },
          { key: 'admin', label: 'Admin', render: (r) => (r.is_super_admin
            ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400">super</span>
            : r.studio_admin
              ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-300">{r.studio_role || 'member'}</span>
              : <span className="text-slate-700">—</span>) },
          { key: 'email_verified', label: 'Verified', render: (r) => (r.email_verified === false
            ? <X size={12} className="text-amber-500" />
            : <Check size={12} className="text-emerald-400" />) },
          { key: 'deactivated', label: 'Status', render: (r) => (r.deactivated
            ? <span className="text-red-400 text-xs">deactivated</span>
            : <span className="text-emerald-400 text-xs">active</span>) },
          { key: 'created_at', label: 'Joined', render: (r) => <span className="text-xs text-slate-500">{r.created_at?.slice(0, 10) || '—'}</span> },
          { key: 'actions', label: '', render: (r) => (
            <div className="flex gap-1 justify-end">
              <button onClick={() => setEditing(r)} data-testid={`manage-user-edit-${r.id}`}
                className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded bg-slate-700 hover:bg-slate-600">
                <PencilSimple size={10} weight="bold" /> Edit
              </button>
              <button onClick={() => setDeleting(r)} disabled={r.id === me?.id} data-testid={`manage-user-delete-${r.id}`}
                title={r.id === me?.id ? "You can't delete your own account" : 'Delete this account and all of its data'}
                className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded bg-red-500/15 text-red-400 hover:bg-red-500/25 disabled:opacity-30 disabled:cursor-not-allowed">
                <Trash size={10} weight="bold" /> Delete
              </button>
            </div>
          ) },
        ]} />
      )}

      {editing && <EditUserModal user={editing} me={me} onClose={() => setEditing(null)} onSaved={onSaved} />}
      {deleting && <DeleteUserModal user={deleting} onClose={() => setDeleting(null)} onDeleted={onDeleted} />}
    </Page>
  );
};

const EditUserModal = ({ user, me, onClose, onSaved }) => {
  const superAdmin = !!me?.is_super_admin;
  const original = useMemo(() => ({
    full_name: user.full_name || '',
    email: user.email || '',
    role: user.role || 'startup',
    studio_admin: !!user.studio_admin,
    studio_role: user.studio_role || 'member',
    studio_active: user.studio_active !== false,
    email_verified: user.email_verified !== false,
    deactivated: !!user.deactivated,
  }), [user]);
  const [form, setForm] = useState(original);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((p) => ({ ...p, [k]: v }));

  // Send only what actually changed — the backend gates privileged fields, so a
  // no-op `role` in the payload would need super admin for a plain name edit.
  const changed = Object.fromEntries(Object.entries(form).filter(([k, v]) => v !== original[k]));
  const dirty = Object.keys(changed).length > 0;

  const save = async (e) => {
    e.preventDefault();
    if (!dirty) { toast.error('Nothing changed'); return; }
    setBusy(true);
    try {
      const r = await api.patch(`/admin/users/${user.id}`, changed);
      toast.success(r.data.synced_rows ? `Saved · ${r.data.synced_rows} linked record(s) updated` : 'Saved');
      onSaved(r.data.user);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally { setBusy(false); }
  };

  return (
    <Modal title="Edit user" subtitle={user.email} onClose={onClose}>
      <form onSubmit={save} className="space-y-3" data-testid="edit-user-form">
        <Field label="Full name">
          <input value={form.full_name} onChange={(e) => set('full_name')(e.target.value)} className={inputCls} data-testid="edit-user-name" />
        </Field>
        <Field label="Email" hint="Changing this also updates the copies stored on their engagements, orders and applications.">
          <input type="email" value={form.email} onChange={(e) => set('email')(e.target.value)} className={inputCls} data-testid="edit-user-email" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Role" hint={superAdmin ? null : 'Super admin only'}>
            <select value={form.role} disabled={!superAdmin} onChange={(e) => set('role')(e.target.value)} className={inputCls} data-testid="edit-user-role">
              {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="Studio role" hint={superAdmin ? null : 'Super admin only'}>
            <select value={form.studio_role} disabled={!superAdmin} onChange={(e) => set('studio_role')(e.target.value)} className={inputCls} data-testid="edit-user-studio-role">
              {STUDIO_ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Toggle label="Admin console access" checked={form.studio_admin} disabled={!superAdmin} onChange={set('studio_admin')} testid="edit-user-studio-admin" />
          <Toggle label="Admin seat active" checked={form.studio_active} onChange={set('studio_active')} testid="edit-user-studio-active" />
          <Toggle label="Email verified" checked={form.email_verified} onChange={set('email_verified')} testid="edit-user-email-verified" />
          <Toggle label="Deactivated" checked={form.deactivated} onChange={set('deactivated')} testid="edit-user-deactivated" />
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
          <button type="button" onClick={onClose} className="text-xs px-3 py-2 border border-slate-700 rounded-lg hover:border-slate-500">Cancel</button>
          <button type="submit" disabled={busy || !dirty} data-testid="edit-user-save"
            className="text-xs px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold rounded-lg disabled:opacity-40">
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

const DeleteUserModal = ({ user, onClose, onDeleted }) => {
  const [cascade, setCascade] = useState(true);
  const [purgeAudit, setPurgeAudit] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [backup, setBackup] = useState(null);   // { filename, docs } once downloaded

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setBackup(null);   // the archive only covers the options it was built with
    api.get(`/admin/users/${user.id}/deletion-preview`, { params: { cascade_entities: cascade, purge_audit_log: purgeAudit } })
      .then((r) => { if (!cancelled) setPreview(r.data); })
      .catch((e) => { if (!cancelled) toast.error(e.response?.data?.detail || 'Could not load preview'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user.id, cascade, purgeAudit]);

  const downloadBackup = async () => {
    setExporting(true);
    try {
      const r = await api.get(`/admin/users/${user.id}/export`, {
        params: { cascade_entities: cascade, purge_audit_log: purgeAudit },
        responseType: 'blob',
      });
      const fromHeader = /filename="?([^";]+)"?/.exec(r.headers['content-disposition'] || '');
      const filename = fromHeader?.[1] || `founderboard-export-${user.id}.zip`;
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      setBackup({ filename, docs: r.headers['x-export-total-documents'] });
      toast.success(`Backup saved · ${filename}`);
    } catch (e) {
      // A blob-typed error body has to be read back as text before it's usable.
      let msg = 'Export failed';
      try { msg = JSON.parse(await e.response?.data?.text())?.detail || msg; } catch { /* keep default */ }
      toast.error(msg);
    } finally { setExporting(false); }
  };

  const rows = Object.entries(preview?.delete_counts || {});
  const detachRows = Object.entries(preview?.detach_counts || {});
  const matches = confirm.trim().toLowerCase() === (user.email || '').toLowerCase();

  const doDelete = async () => {
    setBusy(true);
    try {
      const r = await api.delete(`/admin/users/${user.id}`, {
        params: { confirm_email: confirm.trim(), cascade_entities: cascade, purge_audit_log: purgeAudit, purge_stored_files: true },
      });
      toast.success(`Deleted ${user.email} · ${r.data.total_documents} document(s) removed`);
      onDeleted(user.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally { setBusy(false); }
  };

  return (
    <Modal wide title="Delete account permanently" subtitle={user.email} onClose={onClose}>
      <div className="space-y-4" data-testid="delete-user-modal">
        <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-xs text-red-300">
          <Warning size={14} weight="fill" className="mt-0.5 shrink-0" />
          <span>This cannot be undone. The account row and every child document listed below are deleted outright — there is no soft-delete or restore. Use <strong>Deactivate</strong> on the Users tab if you only want to lock them out.</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Toggle label="Also delete the startup / fund they own" checked={cascade} onChange={setCascade} testid="delete-user-cascade" />
          <Toggle label="Also delete their audit-log history" checked={purgeAudit} onChange={setPurgeAudit} testid="delete-user-purge-audit" />
        </div>
        <p className="text-[10px] text-slate-500 -mt-2">
          {cascade
            ? 'Company data (updates, data room, applications, tracker rows, programs) goes with the account.'
            : 'The company survives — the owner seat is handed to a remaining team member, or left empty if there is none.'}
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-6 justify-center">
            <SpinnerGap size={14} weight="bold" className="animate-spin" /> Working out what this touches…
          </div>
        ) : preview && (
          <>
            {preview.blocked && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 text-xs text-amber-300" data-testid="delete-user-blocked">{preview.blocked}</div>
            )}

            {(preview.owned?.startups?.length > 0 || preview.owned?.vc_funds?.length > 0
              || preview.owned?.vendors?.length > 0 || preview.owned?.agencies?.length > 0
              || preview.memberships?.startups?.length > 0 || preview.memberships?.vc_funds?.length > 0) && (
              <div className="bg-[#0a0e14] border border-slate-800 rounded-xl p-3 space-y-1.5 text-xs">
                {[['Owns startup', preview.owned.startups], ['Owns fund', preview.owned.vc_funds]].map(([label, list]) => (list || []).map((e) => (
                  <div key={e.id} className="flex items-center justify-between">
                    <span className="text-slate-400">{label}</span>
                    <span className="text-slate-200">{e.name || e.id}
                      <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded ${cascade ? 'bg-red-500/15 text-red-400' : 'bg-slate-800 text-slate-400'}`}>{cascade ? 'will be deleted' : 'kept'}</span>
                    </span>
                  </div>
                )))}
                {[['Team member of', preview.memberships.startups], ['Team member of fund', preview.memberships.vc_funds],
                  ['Vendor owner', preview.owned.vendors], ['Agency admin', preview.owned.agencies]].map(([label, list]) => (list || []).map((e) => (
                  <div key={`${label}-${e.id}`} className="flex items-center justify-between">
                    <span className="text-slate-400">{label}</span>
                    <span className="text-slate-200">{e.name || e.id}
                      <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">kept · seat removed</span>
                    </span>
                  </div>
                )))}
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Documents to delete</div>
                <div className="text-xs font-mono text-red-400" data-testid="delete-user-total">{preview.total_documents} total</div>
              </div>
              <div className="bg-[#0a0e14] border border-slate-800 rounded-xl max-h-52 overflow-y-auto divide-y divide-slate-800/60">
                <div className="flex items-center justify-between px-3 py-1.5 text-xs">
                  <span className="font-mono text-slate-300">users</span><span className="font-mono text-slate-400">1</span>
                </div>
                {rows.map(([coll, n]) => (
                  <div key={coll} className="flex items-center justify-between px-3 py-1.5 text-xs" data-testid={`delete-count-${coll}`}>
                    <span className="font-mono text-slate-300">{coll}</span>
                    <span className="font-mono text-slate-400">{n}</span>
                  </div>
                ))}
              </div>
            </div>

            {detachRows.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Kept, with this account&rsquo;s references cleared</div>
                <div className="bg-[#0a0e14] border border-slate-800 rounded-xl max-h-32 overflow-y-auto divide-y divide-slate-800/60">
                  {detachRows.map(([coll, n]) => (
                    <div key={coll} className="flex items-center justify-between px-3 py-1.5 text-xs">
                      <span className="font-mono text-slate-400">{coll}</span>
                      <span className="font-mono text-slate-500">{n}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className={`rounded-xl border px-4 py-3 ${backup ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-[#0a0e14] border-slate-800'}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-slate-200">Download a restore point first</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    A .zip of every document listed above, one Extended-JSON file per collection, with a
                    mongoimport script for re-ingesting into Atlas.
                  </div>
                </div>
                <button type="button" onClick={downloadBackup} disabled={exporting} data-testid="delete-user-export"
                  className="inline-flex items-center gap-1.5 shrink-0 text-[11px] px-3 py-2 rounded-lg border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-40">
                  {exporting
                    ? <><SpinnerGap size={11} weight="bold" className="animate-spin" /> Building…</>
                    : <><DownloadSimple size={11} weight="bold" /> {backup ? 'Download again' : 'Download JSON backup'}</>}
                </button>
              </div>
              {backup ? (
                <div className="flex items-center gap-1.5 mt-2 text-[10px] text-emerald-400" data-testid="delete-user-backup-done">
                  <Check size={10} weight="bold" />
                  <span className="font-mono truncate">{backup.filename}</span>
                  {backup.docs && <span className="text-emerald-600">· {backup.docs} documents</span>}
                </div>
              ) : (
                <div className="flex items-center gap-1.5 mt-2 text-[10px] text-amber-500">
                  <Warning size={10} weight="bold" /> No backup downloaded yet — the delete cannot be undone without one.
                </div>
              )}
            </div>

            <Field label={`Type ${user.email} to confirm`}>
              <input value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="off"
                placeholder={user.email} className={inputCls} data-testid="delete-user-confirm" />
            </Field>
          </>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
          <button type="button" onClick={onClose} className="text-xs px-3 py-2 border border-slate-700 rounded-lg hover:border-slate-500">Cancel</button>
          <button type="button" onClick={doDelete} data-testid="delete-user-confirm-btn"
            disabled={busy || loading || !matches || !preview?.can_delete}
            className="inline-flex items-center gap-1.5 text-xs px-4 py-2 bg-red-500 hover:bg-red-400 text-white font-semibold rounded-lg disabled:opacity-40 disabled:cursor-not-allowed">
            <Trash size={11} weight="bold" /> {busy ? 'Deleting…' : 'Delete permanently'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ─────────────── VENDORS + AGENCIES ───────────────
// Vendors tab has two views, picked by ?view= so a reload or shared link keeps it.
const VENDOR_VIEWS = [
  { key: 'list',        label: 'Vendors' },
  { key: 'engagements', label: 'Engagements' },
];

const VendorSubTabs = () => {
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'engagements' ? 'engagements' : 'list';
  return (
    <div className="flex gap-1" role="tablist">
      {VENDOR_VIEWS.map((v) => (
        <button key={v.key} role="tab" aria-selected={view === v.key}
          onClick={() => setParams(v.key === 'list' ? {} : { view: v.key })}
          data-testid={`vendors-subtab-${v.key}`}
          className={`px-3 py-2 text-xs font-semibold border-b-2 transition-colors ${view === v.key ? 'border-emerald-400 text-emerald-300' : 'border-transparent text-slate-500 hover:text-slate-200'}`}>
          {v.label}
        </button>
      ))}
    </div>
  );
};

const VendorsSection = () => {
  const [params] = useSearchParams();
  return params.get('view') === 'engagements' ? <VendorEngagementsPage /> : <VendorsPage />;
};

const PHASE_META = {
  pre_work:    { label: 'Pre-work',    tone: 'text-sky-400' },
  in_progress: { label: 'In progress', tone: 'text-amber-400' },
  done:        { label: 'Done',        tone: 'text-emerald-400' },
};

const ago = (iso) => {
  if (!iso) return '—';
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return d <= 0 ? 'today' : `${d}d ago`;
};

const money = (n, cur) => (n ? `${cur === 'INR' || !cur ? '₹' : `${cur} `}${Number(n).toLocaleString('en-IN')}` : '—');

const VendorEngagementsPage = () => {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');        // all | issues | pre_work | in_progress | done
  const [vendorId, setVendorId] = useState('');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get('/admin/vendor-engagements'); setData(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not load engagements'); setData({ engagements: [], vendors: [], totals: {} }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    return data.engagements.filter((e) => (
      (!vendorId || e.vendor_id === vendorId)
      && (filter === 'all' || (filter === 'issues' ? e.issues.length > 0 : e.phase === filter))
      && (!needle || [e.founder_startup_name, e.founder_name, e.founder_email, e.vendor_name, e.service]
        .some((s) => (s || '').toLowerCase().includes(needle)))
    ));
  }, [data, filter, vendorId, q]);

  if (!data) return <Page title="Vendors" testid="vendor-engagements-page" tabs={<VendorSubTabs />}><div className="text-sm text-slate-500">Loading engagements…</div></Page>;
  const t = data.totals || {};
  const FILTERS = [
    { key: 'all', label: 'All', n: t.total },
    { key: 'issues', label: 'Needs attention', n: t.with_issues },
    { key: 'pre_work', label: 'Pre-work', n: t.pre_work },
    { key: 'in_progress', label: 'In progress', n: t.in_progress },
    { key: 'done', label: 'Done', n: t.done },
  ];

  return (
    <Page title="Vendors" subtitle="Every founder ↔ vendor engagement — what's underway, what's delivered, and what needs a nudge."
      testid="vendor-engagements-page" tabs={<VendorSubTabs />}
      actions={<button onClick={load} data-testid="vendor-engagements-refresh" className="inline-flex items-center gap-1 text-xs px-3 py-1.5 border border-slate-800 rounded-lg text-slate-300 hover:border-slate-600"><ArrowClockwise size={11} weight="bold" /> Refresh</button>}>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5" data-testid="vendor-engagements-summary">
        <StatCard label="Engagements" value={t.total || 0} sub={`${data.vendors.length} vendors`} tone="slate" />
        <StatCard label="Pre-work" value={t.pre_work || 0} sub="agreement → quote → payment" tone="slate" />
        <StatCard label="In progress" value={t.in_progress || 0} sub="paid, being delivered" tone="amber" />
        <StatCard label="Done" value={t.done || 0} sub="closed by founder" tone="emerald" />
        <StatCard label="Needs attention" value={t.with_issues || 0} sub="overdue, stalled, disputed" tone={t.with_issues ? 'red' : 'emerald'} />
      </div>

      {/* Per-vendor roll-up — click a vendor to scope the list below. */}
      <h2 className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">By vendor</h2>
      <div className="mb-6">
        <Table testid="vendor-engagements-rollup" rows={data.vendors} empty="No engagements yet." cols={[
          { key: 'vendor_name', label: 'Vendor', render: (v) => (
            <button onClick={() => setVendorId(vendorId === v.vendor_id ? '' : v.vendor_id)} data-testid={`engagement-vendor-${v.vendor_id}`}
              className={`text-left hover:text-emerald-300 ${vendorId === v.vendor_id ? 'text-emerald-300 font-semibold' : ''}`}>
              {v.vendor_name || '—'} <span className="ml-1 text-[9px] uppercase px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">{v.vendor_type}</span>
            </button>
          ) },
          { key: 'total', label: 'Total', render: (v) => <span className="font-mono text-xs font-semibold">{v.total}</span> },
          { key: 'pre_work', label: 'Pre-work', render: (v) => <span className="font-mono text-xs text-slate-400">{v.pre_work}</span> },
          { key: 'in_progress', label: 'In progress', render: (v) => <span className={`font-mono text-xs ${v.in_progress ? 'text-amber-400' : 'text-slate-600'}`}>{v.in_progress}</span> },
          { key: 'done', label: 'Done', render: (v) => <span className={`font-mono text-xs ${v.done ? 'text-emerald-400' : 'text-slate-600'}`}>{v.done}</span> },
          { key: 'with_issues', label: 'Issues', render: (v) => <span className={`font-mono text-xs ${v.with_issues ? 'text-red-400 font-semibold' : 'text-slate-600'}`}>{v.with_issues}</span> },
          { key: 'avg_rating', label: 'Avg rating', render: (v) => <span className="text-xs">{v.avg_rating != null ? `★ ${v.avg_rating}` : <span className="text-slate-600">—</span>}</span> },
        ]} />
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-3">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`engagement-filter-${f.key}`}
            className={`text-xs px-3 py-1.5 rounded-lg border ${filter === f.key
              ? (f.key === 'issues' ? 'border-red-500/50 bg-red-500/15 text-red-300' : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300')
              : 'border-slate-800 text-slate-400 hover:border-slate-600'}`}>
            {f.label} <span className="font-mono ml-1 opacity-80">{f.n || 0}</span>
          </button>
        ))}
        <div className="flex-1" />
        {vendorId && (
          <button onClick={() => setVendorId('')} className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-800 text-slate-300" data-testid="engagement-vendor-clear">
            {data.vendors.find((v) => v.vendor_id === vendorId)?.vendor_name} <X size={10} weight="bold" />
          </button>
        )}
        <div className="relative">
          <MagnifyingGlass size={11} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search startup, founder, service…" data-testid="engagement-search"
            className="bg-[#111827] border border-slate-800 rounded-lg pl-7 pr-3 py-1.5 text-xs w-60" />
        </div>
      </div>

      <Table testid="vendor-engagements-table" rows={rows} empty="No engagements match."
        renderExpanded={(e) => openId === e.id && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-xs" data-testid={`engagement-detail-${e.id}`}>
            {[
              ['Founder', `${e.founder_name || '—'} · ${e.founder_email || ''}`],
              ['Flow', e.flow === 'guided' ? 'Guided service request' : 'Legacy intro'],
              ['Started', e.created_at?.slice(0, 10) || '—'],
              ['Paid', e.paid_at?.slice(0, 10) || '—'],
              ['Delivery due', e.due_at?.slice(0, 10) || '—'],
              ['Delivered', e.delivered_at?.slice(0, 10) || '—'],
              ['Closed', e.closed_at?.slice(0, 10) || '—'],
              ['Revisions · messages', `${e.revisions_count} · ${e.messages}`],
              ['Founder rating', e.rating != null ? `★ ${e.rating}/5` : '—'],
            ].map(([k, v]) => (
              <div key={k}><div className="text-[10px] uppercase tracking-wider text-slate-500">{k}</div><div className="text-slate-200 break-all">{v}</div></div>
            ))}
          </div>
        )}
        cols={[
          { key: 'founder_startup_name', label: 'Startup', render: (e) => (
            <button onClick={() => setOpenId(openId === e.id ? null : e.id)} data-testid={`engagement-expand-${e.id}`}
              className="flex items-center gap-1.5 text-left hover:text-emerald-300">
              {openId === e.id ? <CaretDown size={11} weight="bold" className="text-emerald-400 shrink-0" /> : <CaretRight size={11} weight="bold" className="text-slate-500 shrink-0" />}
              <span className="truncate max-w-[180px]">{e.founder_startup_name || e.founder_name || '—'}</span>
            </button>
          ) },
          { key: 'vendor_name', label: 'Vendor', render: (e) => <span className="text-xs">{e.vendor_name}</span> },
          { key: 'service', label: 'Service', render: (e) => <span className="text-xs text-slate-300">{e.service || '—'}</span> },
          { key: 'status', label: 'Stage', render: (e) => (
            <div>
              <div className={`text-[10px] uppercase tracking-wider font-bold ${PHASE_META[e.phase]?.tone || 'text-slate-400'}`}>{PHASE_META[e.phase]?.label || e.phase}</div>
              <div className="text-[11px] text-slate-500">{(e.status || '').replace(/_/g, ' ')}</div>
            </div>
          ) },
          { key: 'value', label: 'Value', render: (e) => <span className="font-mono text-xs">{money(e.value, e.currency)}</span> },
          { key: 'last_activity_at', label: 'Last activity', render: (e) => <span className="text-xs text-slate-400">{ago(e.last_activity_at || e.created_at)}</span> },
          { key: 'issues', label: 'Issues', render: (e) => (e.issues.length ? (
            <div className="flex flex-wrap gap-1 max-w-[260px]">
              {e.issues.map((i) => (
                <span key={i.code} className={`text-[10px] px-1.5 py-0.5 rounded ${i.severity === 'high' ? 'bg-red-500/15 text-red-400' : 'bg-amber-500/15 text-amber-400'}`}>{i.label}</span>
              ))}
            </div>
          ) : <span className="text-[10px] text-emerald-400">On track</span>) },
        ]} />
    </Page>
  );
};

const VendorsPage = () => {
  const [vs, setVs] = useState([]);
  const load = async () => {
    const r2 = await api.get('/admin/vendors', { params: { vendor_type: 'service' } }).catch(() => ({ data: { vendors: [] } }));
    const r3 = await api.get('/admin/vendors', { params: { vendor_type: 'credit' } }).catch(() => ({ data: { vendors: [] } }));
    setVs([...(r2.data.vendors || []), ...(r3.data.vendors || [])]);
  };
  useEffect(() => { load(); }, []);
  const patch = async (v, status) => {
    if (!window.confirm(`Set status to '${status}' for ${v.name}?`)) return;
    try { await api.patch(`/admin/vendors/${v.id}`, { status }); toast.success('Done'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const act = async (v, path) => {
    if (!window.confirm(`${path} ${v.name}?`)) return;
    try { await api.post(`/admin/vendors/${v.id}/${path}`, {}); toast.success('Done'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <Page title="Vendors" subtitle="Services + credit vendors. Approve applications, suspend, or reinstate." testid="vendors-page" tabs={<VendorSubTabs />}>
      <Table testid="vendors-table" cols={[
        { key: 'name', label: 'Name' },
        { key: 'vendor_type', label: 'Type', render: (r) => <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-slate-800">{r.vendor_type}</span> },
        { key: 'status', label: 'Status', render: (r) => <span className={`text-xs ${r.status === 'approved' ? 'text-emerald-400' : r.status === 'paused' ? 'text-amber-400' : r.status === 'pending_review' ? 'text-blue-400' : 'text-slate-500'}`}>{r.status}</span> },
        { key: 'contact_email', label: 'Contact', render: (r) => <span className="text-xs font-mono">{r.contact_email}</span> },
        { key: 'actions', label: '', render: (r) => (
          <div className="flex gap-1">
            {r.status === 'pending_review' && (
              <>
                <button onClick={() => patch(r, 'approved')} className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25" data-testid={`vendor-approve-${r.id}`}>Approve</button>
                <button onClick={() => patch(r, 'rejected')} className="text-[10px] px-2 py-0.5 rounded bg-red-500/15 text-red-400 hover:bg-red-500/25" data-testid={`vendor-reject-${r.id}`}>Reject</button>
              </>
            )}
            {r.status === 'approved' && (
              <button onClick={() => act(r, 'suspend')} className="text-[10px] px-2 py-0.5 rounded bg-red-500/15 text-red-400" data-testid={`vendor-suspend-${r.id}`}>Suspend</button>
            )}
            {r.status === 'paused' && (
              <button onClick={() => act(r, 'reinstate')} className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400" data-testid={`vendor-reinstate-${r.id}`}>Reinstate</button>
            )}
          </div>
        )},
      ]} rows={vs} empty="No vendors." />
    </Page>
  );
};

const AgenciesPage = () => {
  const [ags, setAgs] = useState([]);
  const load = async () => {
    const r = await api.get('/admin/vendors', { params: { vendor_type: 'agency' } }).catch(() => ({ data: { vendors: [] } }));
    setAgs(r.data.vendors || []);
  };
  useEffect(() => { load(); }, []);
  const patch = async (a, status) => {
    if (!window.confirm(`Set status to '${status}' for ${a.name}?`)) return;
    try { await api.patch(`/admin/vendors/${a.id}`, { status }); toast.success('Done'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const act = async (a, path) => {
    if (!window.confirm(`${path} ${a.name}?`)) return;
    try { await api.post(`/admin/agencies/${a.id}/${path}`, {}); toast.success('Done'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <Page title="Agencies" subtitle="Marketing agencies. Approve applications, suspend, or reinstate." testid="agencies-page">
      <Table testid="agencies-table" cols={[
        { key: 'name', label: 'Name' },
        { key: 'status', label: 'Status', render: (r) => <span className={`text-xs ${r.status === 'approved' ? 'text-emerald-400' : r.status === 'paused' ? 'text-amber-400' : r.status === 'pending_review' ? 'text-blue-400' : 'text-slate-500'}`}>{r.status}</span> },
        { key: 'contact_email', label: 'Contact', render: (r) => <span className="text-xs font-mono">{r.contact_email}</span> },
        { key: 'tagline', label: 'Tagline', render: (r) => <span className="text-xs text-slate-400 line-clamp-1">{r.tagline || '—'}</span> },
        { key: 'actions', label: '', render: (r) => (
          <div className="flex gap-1">
            {r.status === 'pending_review' && (
              <>
                <button onClick={() => patch(r, 'approved')} className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25" data-testid={`agency-approve-${r.id}`}>Approve</button>
                <button onClick={() => patch(r, 'rejected')} className="text-[10px] px-2 py-0.5 rounded bg-red-500/15 text-red-400 hover:bg-red-500/25" data-testid={`agency-reject-${r.id}`}>Reject</button>
              </>
            )}
            {r.status === 'approved' && r.from !== 'agency_invitations' && (
              <button onClick={() => act(r, 'suspend')} className="text-[10px] px-2 py-0.5 rounded bg-red-500/15 text-red-400" data-testid={`agency-suspend-${r.id}`}>Suspend</button>
            )}
            {r.status === 'paused' && (
              <button onClick={() => act(r, 'reinstate')} className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400" data-testid={`agency-reinstate-${r.id}`}>Reinstate</button>
            )}
          </div>
        )},
      ]} rows={ags} empty="No agencies." />
    </Page>
  );
};

// ─────────────── CATALOG ───────────────
const CatalogPage = () => {
  const [items, setItems] = useState([]); const [cat, setCat] = useState('service'); const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', description: '' });
  const load = async () => { const r = await api.get('/admin/catalog/all'); setItems(r.data.items || []); };
  useEffect(() => { load(); }, []);
  const create = async (e) => { e.preventDefault(); try { await api.post('/admin/catalog', { category: cat, ...form }); toast.success('Added'); setForm({ name: '', description: '' }); setCreating(false); load(); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); } };
  const [reviewing, setReviewing] = useState(null);   // {id, name, description} being verified
  const [approving, setApproving] = useState(false);
  const [pendingOnly, setPendingOnly] = useState(false);
  // Vendor suggestions sit at status "pending" until an admin verifies them.
  const isSuggestion = (i) => i.status === 'pending' || i.status === 'rejected';
  const del = async (i) => {
    const msg = isSuggestion(i) ? `Delete the suggestion "${i.name}"?` : `Deprecate "${i.name}"?`;
    if (!window.confirm(msg)) return;
    try {
      const r = await api.delete(`/admin/catalog/${i.id}`);
      toast.success(r.data?.deleted ? 'Suggestion deleted' : 'Deprecated');
      if (reviewing?.id === i.id) setReviewing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const approve = async () => {
    setApproving(true);
    try {
      await api.post(`/admin/catalog/${reviewing.id}/approve`, { name: reviewing.name, description: reviewing.description });
      toast.success('Approved — now live in the marketplace');
      setReviewing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setApproving(false); }
  };
  const pendingCount = useMemo(() => items.filter((i) => i.status === 'pending').length, [items]);
  // Suggestions awaiting review float to the top of their category.
  const filtered = useMemo(() => items
    .filter((i) => (pendingOnly ? i.status === 'pending' : i.category === cat))
    .sort((a, b) => (b.status === 'pending') - (a.status === 'pending')), [items, cat, pendingOnly]);
  return (
    <Page title="Catalog" subtitle={`${items.length} products across services, credit & agency marketplaces.`} testid="catalog-page"
      actions={<>
        <button onClick={() => setPendingOnly((x) => !x)} data-testid="catalog-pending-toggle"
          className={`text-xs px-3 py-1.5 rounded-lg border ${pendingOnly ? 'border-amber-500/50 bg-amber-500/15 text-amber-300' : 'border-slate-800 text-slate-300 hover:border-slate-600'}`}>
          Pending suggestions{pendingCount > 0 && <span className="ml-1.5 font-mono font-bold text-amber-400">{pendingCount}</span>}
        </button>
        <select value={cat} onChange={(e) => { setCat(e.target.value); setPendingOnly(false); }} data-testid="catalog-cat"
          className="bg-[#111827] border border-slate-800 rounded-lg px-2 py-1.5 text-xs">{['service', 'credit', 'agency'].map((c) => <option key={c}>{c}</option>)}</select>
        <button onClick={() => setCreating(true)} data-testid="catalog-add-btn" className="inline-flex items-center gap-1 bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-semibold px-3 py-1.5 rounded-lg"><Plus size={11} weight="bold" /> Add item</button>
      </>}>
      {creating && (
        <form onSubmit={create} className="bg-[#111827] border border-emerald-500/30 rounded-xl p-4 mb-4 space-y-2" data-testid="catalog-form">
          <input required placeholder="Product name" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} data-testid="catalog-name" className="w-full bg-[#0a0e14] border border-slate-800 rounded-lg px-3 py-2 text-sm" />
          <textarea placeholder="Description" value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} rows={2} data-testid="catalog-desc" className="w-full bg-[#0a0e14] border border-slate-800 rounded-lg px-3 py-2 text-sm" />
          <div className="flex gap-2 justify-end"><button type="button" onClick={() => setCreating(false)} className="text-xs px-3 py-1.5 border border-slate-700 rounded-lg">Cancel</button><button type="submit" data-testid="catalog-save" className="text-xs px-3 py-1.5 bg-emerald-500 text-black font-semibold rounded-lg">Save</button></div>
        </form>
      )}
      <Table testid="catalog-table" cols={[
        { key: 'name', label: 'Name' },
        { key: 'category', label: 'Category', render: (r) => <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-slate-800">{r.category}</span> },
        { key: 'status', label: 'Status', render: (r) => (
          <div>
            <span className={`text-xs ${r.status === 'approved' ? 'text-emerald-400' : r.status === 'deprecated' ? 'text-slate-600' : 'text-amber-400'}`}>{r.status}</span>
            {r.suggested_by_org_name ? (
              <div className="text-[11px] text-slate-300" data-testid={`catalog-suggested-by-${r.id}`}>
                {r.suggested_by_org_name}
                <span className="ml-1 text-[9px] uppercase tracking-wider text-slate-500">{r.suggested_by_org_type}</span>
              </div>
            ) : r.suggested_by_user_id && (
              // The suggester's account isn't linked to any vendor or agency (e.g. an admin).
              <div className="text-[10px] italic text-slate-500" data-testid={`catalog-suggested-by-${r.id}`}>Not linked to a vendor</div>
            )}
            {r.suggested_by_email && <div className="text-[10px] text-slate-500 font-mono">by {r.suggested_by_email}</div>}
          </div>
        ) },
        { key: 'description', label: 'Description', render: (r) => <span className="text-xs text-slate-400 line-clamp-1">{r.description}</span> },
        { key: 'actions', label: '', render: (r) => r.status !== 'deprecated' && (
          <div className="flex items-center gap-1.5 justify-end">
            {isSuggestion(r) && (
              <button onClick={() => setReviewing(reviewing?.id === r.id ? null : { id: r.id, name: r.name || '', description: r.description || '' })}
                className="text-[10px] px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 hover:bg-amber-500/25" data-testid={`catalog-review-${r.id}`}>
                {reviewing?.id === r.id ? 'Close' : 'Review & verify'}
              </button>
            )}
            {isSuggestion(r) ? (
              <button onClick={() => del(r)} className="text-[10px] px-2 py-0.5 rounded bg-red-500/15 text-red-400 hover:bg-red-500/25" data-testid={`catalog-del-${r.id}`}>Delete</button>
            ) : (
              <button onClick={() => del(r)} className="text-red-400 hover:text-red-300" data-testid={`catalog-del-${r.id}`}><TrashSimple size={11} weight="bold" /></button>
            )}
          </div>
        ) },
      ]} rows={filtered} empty={pendingOnly ? 'No suggestions waiting for review.' : 'No items in this category.'}
        renderExpanded={(r) => reviewing?.id === r.id && (
          <div className="space-y-2 max-w-2xl" data-testid={`catalog-review-panel-${r.id}`}>
            <div className="text-[10px] uppercase tracking-wider font-bold text-amber-300">Verify suggestion</div>
            <p className="text-[11px] text-slate-500">
              Suggested{r.suggested_by_org_name ? <> by <span className="text-slate-300 font-semibold">{r.suggested_by_org_name}</span> ({r.suggested_by_org_type})</> : ''}{r.suggested_by_email ? ` · ${r.suggested_by_email}` : ''}{r.created_at ? ` on ${new Date(r.created_at).toLocaleDateString()}` : ''} for the <span className="text-slate-300">{r.category}</span> marketplace.
              Fix the name or description if needed, then approve to make it available to every vendor.
            </p>
            <input value={reviewing.name} onChange={(e) => setReviewing((p) => ({ ...p, name: e.target.value }))} placeholder="Product name"
              data-testid="catalog-review-name" className="w-full bg-[#111827] border border-slate-800 rounded-lg px-3 py-2 text-sm" />
            <textarea value={reviewing.description} onChange={(e) => setReviewing((p) => ({ ...p, description: e.target.value }))} rows={2} placeholder="Description"
              data-testid="catalog-review-desc" className="w-full bg-[#111827] border border-slate-800 rounded-lg px-3 py-2 text-sm" />
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => del(r)} data-testid="catalog-review-delete"
                className="text-xs px-3 py-1.5 border border-red-500/40 text-red-400 rounded-lg hover:bg-red-500/10">Delete</button>
              <button type="button" onClick={approve} disabled={approving || reviewing.name.trim().length < 3} data-testid="catalog-review-approve"
                className="inline-flex items-center gap-1 text-xs px-3 py-1.5 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold rounded-lg disabled:opacity-50">
                <Check size={11} weight="bold" /> {approving ? 'Approving…' : 'Verify & approve'}
              </button>
            </div>
          </div>
        )} />
    </Page>
  );
};

// ─────────────── VAULT DOCUMENT REQUESTS ───────────────
// Vendors ask for documents missing from the vault list. Approving one adds it as
// a new slot under the chosen category — in the vendor required-docs picker and
// in every founder's vault. The admin can fix the name or category first.
const VAULT_REQ_TONE = { pending: 'text-amber-400', approved: 'text-emerald-400', rejected: 'text-red-400' };

const VaultDocRequestsPage = () => {
  const [rows, setRows] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [status, setStatus] = useState('pending');
  const [reviewing, setReviewing] = useState(null);   // {id, name, dataroom_id, reason}
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const r = await api.get('/admin/vault/document-requests', { params: status ? { status } : {} });
      setRows(r.data.requests || []); setRooms(r.data.datarooms || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to load'); }
  }, [status]);
  useEffect(() => { load(); }, [load]);
  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/vault/document-requests/${reviewing.id}/approve`, { name: reviewing.name, dataroom_id: reviewing.dataroom_id });
      toast.success('Approved — now in the vault list');
      setReviewing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const reject = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/vault/document-requests/${reviewing.id}/reject`, { reason: reviewing.reason });
      toast.success('Rejected');
      setReviewing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Page title="Vault Documents" subtitle="Documents vendors asked to add to the vault list. Approved ones become a new slot in that category for every vendor and founder." testid="vault-docs-page"
      actions={
        <select value={status} onChange={(e) => { setStatus(e.target.value); setReviewing(null); }} data-testid="vault-docs-status"
          className="bg-[#111827] border border-slate-800 rounded-lg px-2 py-1.5 text-xs">
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="">All</option>
        </select>
      }>
      <Table testid="vault-docs-table" cols={[
        { key: 'name', label: 'Document', render: (r) => (
          <div>
            <div className="text-sm">{r.name}</div>
            {r.description && <div className="text-[11px] text-slate-500 line-clamp-2">{r.description}</div>}
          </div>
        ) },
        { key: 'dataroom_name', label: 'Category', render: (r) => <span className="text-xs text-slate-300">{r.dataroom_name}</span> },
        { key: 'vendor', label: 'Requested by', render: (r) => (
          <div>
            <div className="text-xs text-slate-300">{r.vendor_name || '—'}</div>
            {r.requested_by_email && <div className="text-[10px] text-slate-500 font-mono">{r.requested_by_email}</div>}
          </div>
        ) },
        { key: 'status', label: 'Status', render: (r) => (
          <div>
            <span className={`text-xs ${VAULT_REQ_TONE[r.status] || 'text-slate-400'}`}>{r.status}</span>
            {r.status === 'rejected' && r.reject_reason && <div className="text-[10px] text-slate-500 line-clamp-1">{r.reject_reason}</div>}
          </div>
        ) },
        { key: 'created_at', label: 'Requested', render: (r) => <span className="text-xs text-slate-500">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</span> },
        { key: 'actions', label: '', render: (r) => r.status !== 'approved' && (
          <div className="flex justify-end">
            <button onClick={() => setReviewing(reviewing?.id === r.id ? null : { id: r.id, name: r.name || '', dataroom_id: r.dataroom_id, reason: '' })}
              className="text-[10px] px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 hover:bg-amber-500/25" data-testid={`vault-docs-review-${r.id}`}>
              {reviewing?.id === r.id ? 'Close' : 'Review'}
            </button>
          </div>
        ) },
      ]} rows={rows} empty={status === 'pending' ? 'No document requests waiting for review.' : 'No requests.'}
        renderExpanded={(r) => reviewing?.id === r.id && (
          <div className="space-y-2 max-w-2xl" data-testid={`vault-docs-review-panel-${r.id}`}>
            <div className="text-[10px] uppercase tracking-wider font-bold text-amber-300">Verify document request</div>
            <p className="text-[11px] text-slate-500">Fix the name or category if needed. Approving adds it to the vault list straight away.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <input value={reviewing.name} onChange={(e) => setReviewing((p) => ({ ...p, name: e.target.value }))} placeholder="Document name" maxLength={140}
                data-testid="vault-docs-review-name" className={inputCls} />
              <select value={reviewing.dataroom_id} onChange={(e) => setReviewing((p) => ({ ...p, dataroom_id: e.target.value }))}
                data-testid="vault-docs-review-category" className={inputCls}>
                {rooms.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            {r.status === 'pending' && (
              <input value={reviewing.reason} onChange={(e) => setReviewing((p) => ({ ...p, reason: e.target.value }))} placeholder="Reason (shown to the vendor if you reject)" maxLength={600}
                data-testid="vault-docs-review-reason" className={inputCls} />
            )}
            <div className="flex gap-2 justify-end">
              {r.status === 'pending' && (
                <button type="button" onClick={reject} disabled={busy} data-testid="vault-docs-review-reject"
                  className="text-xs px-3 py-1.5 border border-red-500/40 text-red-400 rounded-lg hover:bg-red-500/10 disabled:opacity-50">Reject</button>
              )}
              <button type="button" onClick={approve} disabled={busy || reviewing.name.trim().length < 3} data-testid="vault-docs-review-approve"
                className="inline-flex items-center gap-1 text-xs px-3 py-1.5 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold rounded-lg disabled:opacity-50">
                <Check size={11} weight="bold" /> {busy ? 'Saving…' : 'Approve & add to vault'}
              </button>
            </div>
          </div>
        )} />
    </Page>
  );
};

// ─────────────── COUPONS ───────────────
const CouponsPage = () => {
  const [coupons, setCoupons] = useState([]); const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ code: '', kind: 'percent', value: 10, scope: 'platform', description: '', role_scope: '', user_emails: '' });
  const load = async () => { const r = await api.get('/admin/coupons'); setCoupons(r.data.coupons || []); };
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/coupons', {
        ...form, code: form.code.toUpperCase(), value: Number(form.value),
        user_emails: form.scope === 'user_whitelist' ? form.user_emails.split(',').map((s) => s.trim()).filter(Boolean) : [],
        role_scope: form.scope === 'role' ? form.role_scope : null,
      });
      toast.success('Coupon created'); setCreating(false); load();
      setForm({ code: '', kind: 'percent', value: 10, scope: 'platform', description: '', role_scope: '', user_emails: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const toggle = async (c) => { try { await api.patch(`/admin/coupons/${c.id}`, { active: !c.active }); load(); } catch (e) { toast.error('Failed'); } };
  const del = async (c) => { if (!window.confirm(`Delete ${c.code}?`)) return; await api.delete(`/admin/coupons/${c.id}`); load(); };

  return (
    <Page title="Coupons & Promotions" subtitle="Discount codes for engagement billing charges." testid="coupons-page"
      actions={<button onClick={() => setCreating(true)} data-testid="coupon-add-btn" className="inline-flex items-center gap-1 bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-semibold px-3 py-1.5 rounded-lg"><Plus size={11} weight="bold" /> New coupon</button>}>
      {creating && (
        <form onSubmit={create} className="bg-[#111827] border border-emerald-500/30 rounded-xl p-4 mb-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="coupon-form">
          <input required placeholder="CODE (e.g. WELCOME20)" value={form.code} onChange={(e) => setForm((p) => ({ ...p, code: e.target.value.toUpperCase() }))} data-testid="coupon-code" className="bg-[#0a0e14] border border-slate-800 rounded-lg px-3 py-2 text-sm font-mono" />
          <select value={form.kind} onChange={(e) => setForm((p) => ({ ...p, kind: e.target.value }))} data-testid="coupon-kind" className="bg-[#0a0e14] border border-slate-800 rounded-lg px-3 py-2 text-sm"><option value="percent">Percent off</option><option value="flat">Flat ₹ off</option></select>
          <input required type="number" placeholder={form.kind === 'percent' ? '10 (means 10%)' : '500 (means ₹500)'} value={form.value} onChange={(e) => setForm((p) => ({ ...p, value: e.target.value }))} data-testid="coupon-value" className="bg-[#0a0e14] border border-slate-800 rounded-lg px-3 py-2 text-sm" />
          <select value={form.scope} onChange={(e) => setForm((p) => ({ ...p, scope: e.target.value }))} data-testid="coupon-scope" className="bg-[#0a0e14] border border-slate-800 rounded-lg px-3 py-2 text-sm">
            <option value="platform">Platform-wide (public code)</option><option value="user_whitelist">Specific users</option><option value="role">Role-based</option>
          </select>
          {form.scope === 'user_whitelist' && (
            <input placeholder="user1@ex.com, user2@ex.com" value={form.user_emails} onChange={(e) => setForm((p) => ({ ...p, user_emails: e.target.value }))} data-testid="coupon-users" className="md:col-span-2 bg-[#0a0e14] border border-slate-800 rounded-lg px-3 py-2 text-sm" />
          )}
          {form.scope === 'role' && (
            <select value={form.role_scope} onChange={(e) => setForm((p) => ({ ...p, role_scope: e.target.value }))} data-testid="coupon-role-scope" className="bg-[#0a0e14] border border-slate-800 rounded-lg px-3 py-2 text-sm"><option value="">-- pick role --</option>{['startup', 'vc', 'vendor'].map((r) => <option key={r}>{r}</option>)}</select>
          )}
          <input placeholder="Description (internal)" value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} data-testid="coupon-desc" className="md:col-span-2 bg-[#0a0e14] border border-slate-800 rounded-lg px-3 py-2 text-sm" />
          <div className="md:col-span-2 flex justify-end gap-2"><button type="button" onClick={() => setCreating(false)} className="text-xs px-3 py-1.5 border border-slate-700 rounded-lg">Cancel</button><button type="submit" data-testid="coupon-save" className="text-xs px-3 py-1.5 bg-emerald-500 text-black font-semibold rounded-lg">Save coupon</button></div>
        </form>
      )}
      <Table testid="coupons-table" cols={[
        { key: 'code', label: 'Code', render: (r) => <span className="font-mono text-emerald-400 font-bold">{r.code}</span> },
        { key: 'kind', label: 'Off', render: (r) => <span className="text-xs">{r.kind === 'percent' ? `${r.value}%` : `₹${r.value}`}</span> },
        { key: 'scope', label: 'Scope', render: (r) => <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-slate-800">{r.scope}{r.role_scope ? ` · ${r.role_scope}` : ''}</span> },
        { key: 'redemption_count', label: 'Used', render: (r) => <span className="font-mono text-xs">{r.redemption_count || 0}{r.max_redemptions ? `/${r.max_redemptions}` : ''}</span> },
        { key: 'active', label: 'Active', render: (r) => (<button onClick={() => toggle(r)} data-testid={`coupon-toggle-${r.id}`} className={`text-[10px] px-2 py-0.5 rounded ${r.active ? 'bg-emerald-500/15 text-emerald-400' : 'bg-slate-800 text-slate-500'}`}>{r.active ? 'on' : 'off'}</button>) },
        { key: 'actions', label: '', render: (r) => <button onClick={() => del(r)} data-testid={`coupon-del-${r.id}`} className="text-red-400 hover:text-red-300"><TrashSimple size={11} weight="bold" /></button> },
      ]} rows={coupons} empty="No coupons yet." />
    </Page>
  );
};

// ─────────────── PROGRAMS + PAYMENTS + BROADCAST + AUDIT + FLAGS + SYSTEM + ADMINS ───────────────
// One applicant line inside the expanded drawer — name + org on top, contact below.
const ApplicantCard = ({ a, testid }) => (
  <div className="bg-[#111827] border border-slate-800 rounded-lg px-3 py-2" data-testid={testid}>
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-xs font-medium text-slate-100 truncate min-w-0">{a.name || '—'}</span>
      {a.at && <span className="text-[10px] text-slate-600 shrink-0">{a.at.slice(0, 10)}</span>}
    </div>
    {a.startup_name && <div className="text-[10px] text-slate-500 truncate">{a.startup_name}</div>}
    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] font-mono">
      {a.email
        ? <a href={`mailto:${a.email}`} className="text-emerald-400 hover:underline break-all">{a.email}</a>
        : <span className="text-slate-600">no email</span>}
      {a.phone
        ? <a href={`tel:${a.phone}`} className="text-slate-300 hover:underline">{formatPhone(a.phone)}</a>
        : <span className="text-slate-600">no phone</span>}
    </div>
    <div className="mt-1 flex flex-wrap gap-1">
      {a.channel && (
        <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-800 ${CHANNEL_META[a.channel]?.tone || 'text-slate-400'}`}
          data-testid={`${testid}-channel`}>
          {CHANNEL_META[a.channel]?.label || a.channel}
        </span>
      )}
      {a.status && (
        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">{a.status}</span>
      )}
    </div>
  </div>
);

// How an application reached the program — see _channel() in admin_program_applicants.
const CHANNEL_META = {
  quick_apply:  { label: 'Quick Apply',      tone: 'text-amber-400' },
  founderboard: { label: 'Via Founderboard', tone: 'text-emerald-400' },
  imported:     { label: 'Imported by VC',   tone: 'text-slate-400' },
};

const ApplicantColumn = ({ label, tone, people, emptyText, testid }) => (
  <div data-testid={testid}>
    <div className={`text-[10px] uppercase tracking-wider font-bold mb-2 ${tone}`}>{label} · {people.length}</div>
    {people.length === 0 ? (
      <div className="text-xs text-slate-600">{emptyText}</div>
    ) : (
      <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
        {people.map((a) => <ApplicantCard key={a.id} a={a} testid={`applicant-${a.id}`} />)}
      </div>
    )}
  </div>
);

const ProgramsPage = () => {
  const [rows, setRows] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [details, setDetails] = useState({});   // program_id -> {submitted, drafts}
  const [loadingId, setLoadingId] = useState(null);

  useEffect(() => { api.get('/admin/programs').then((r) => setRows(r.data.programs || [])); }, []);

  const toggle = async (r) => {
    if (openId === r.id) { setOpenId(null); return; }
    setOpenId(r.id);
    if (details[r.id]) return;
    setLoadingId(r.id);
    try {
      const res = await api.get(`/admin/programs/${r.id}/applicants`);
      setDetails((p) => ({ ...p, [r.id]: { submitted: res.data.submitted || [], drafts: res.data.drafts || [] } }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load applicants');
      setOpenId(null);
    } finally { setLoadingId(null); }
  };

  const sum = (k) => rows.reduce((n, r) => n + (r[k] || 0), 0);
  const totalDrafts = sum('draft_count');
  const totalApps = sum('application_count');
  const totalQuick = sum('quick_apply_count');
  const totalFb = sum('founderboard_count');
  const totalImported = sum('imported_count');
  const pct = (n) => (totalApps ? `${Math.round((n / totalApps) * 100)}% of submitted` : '—');

  const drawer = (r) => {
    if (openId !== r.id) return null;
    if (loadingId === r.id) return <div className="text-xs text-slate-500">Loading applicants…</div>;
    const d = details[r.id];
    if (!d) return null;
    const draftsBy = (ch) => d.drafts.filter((a) => a.channel === ch).length;
    // Per-program channel summary: submitted counts come from the table row,
    // draft split from the applicant list just loaded.
    const tiles = [
      { label: 'Submitted',        value: r.application_count || 0,  tone: 'text-slate-100' },
      { label: 'Quick Apply',      value: r.quick_apply_count || 0,  tone: 'text-amber-400',   sub: `${draftsBy('quick_apply')} in draft` },
      { label: 'Via Founderboard', value: r.founderboard_count || 0, tone: 'text-emerald-400', sub: `${draftsBy('founderboard')} in draft` },
      ...(r.imported_count ? [{ label: 'Imported by VC', value: r.imported_count, tone: 'text-slate-300' }] : []),
    ];
    return (
      <div className="space-y-4">
      <div className="flex flex-wrap gap-2" data-testid={`program-summary-${r.id}`}>
        {tiles.map((t) => (
          <div key={t.label} className="bg-[#111827] border border-slate-800 rounded-lg px-3 py-2 min-w-[130px]">
            <div className="text-[9px] uppercase tracking-wider font-bold text-slate-500">{t.label}</div>
            <div className={`text-lg font-heading font-bold ${t.tone}`}>{t.value}</div>
            {t.sub && <div className="text-[10px] text-slate-500">{t.sub}</div>}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid={`program-applicants-${r.id}`}>
        <ApplicantColumn label="Submitted" tone="text-emerald-400" people={d.submitted}
          emptyText="Nobody has submitted yet." testid={`program-submitted-${r.id}`} />
        <ApplicantColumn label="In draft" tone="text-amber-400" people={d.drafts}
          emptyText="No applications in progress." testid={`program-drafts-${r.id}`} />
      </div>
      </div>
    );
  };

  return (
    <Page title="Programs" subtitle={`${rows.length} programs across every VC / incubator · ${totalDrafts} applications sitting in draft.`} testid="programs-page">
      {/* Where submitted applications came from, across every program. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="programs-channel-summary">
        <StatCard label="Submitted" value={totalApps} sub={`${totalDrafts} more in draft`} tone="slate" />
        <StatCard label="Quick Apply" value={totalQuick} sub={pct(totalQuick)} tone="amber" />
        <StatCard label="Via Founderboard" value={totalFb} sub={pct(totalFb)} tone="emerald" />
        <StatCard label="Imported by VC" value={totalImported} sub={pct(totalImported)} tone="slate" />
      </div>
      <Table testid="programs-table" rows={rows} empty="No programs." renderExpanded={drawer} cols={[
        { key: 'name', label: 'Program', render: (r) => (
          <button onClick={() => toggle(r)} data-testid={`program-expand-${r.id}`}
            className="flex items-center gap-1.5 text-left hover:text-emerald-300">
            {openId === r.id ? <CaretDown size={11} weight="bold" className="text-emerald-400 shrink-0" /> : <CaretRight size={11} weight="bold" className="text-slate-500 shrink-0" />}
            {r.name}
          </button>
        ) },
        { key: 'fund_name', label: 'Fund / Incubator', render: (r) => (r.fund_name
          ? <div className="flex items-center gap-1.5 min-w-0">
              <span className="text-xs truncate max-w-[180px]" title={r.fund_name}>{r.fund_name}</span>
              <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0 ${ORG_TONE[r.entity_type] || ORG_TONE.vc}`}>{r.entity_type || 'vc'}</span>
            </div>
          : <span className="text-slate-600 text-xs">—</span>) },
        { key: 'status', label: 'Status', render: (r) => <span className={`text-xs ${r.status === 'open' ? 'text-emerald-400' : r.status === 'closed' ? 'text-slate-500' : 'text-amber-400'}`}>{r.status}</span> },
        { key: 'application_count', label: 'Apps', render: (r) => <span className="font-mono text-xs font-semibold">{r.application_count}</span> },
        { key: 'quick_apply_count', label: 'Quick Apply', render: (r) => (
          <span className={`font-mono text-xs ${r.quick_apply_count ? 'text-amber-400' : 'text-slate-600'}`} data-testid={`program-quick-${r.id}`}>{r.quick_apply_count || 0}</span>
        ) },
        { key: 'founderboard_count', label: 'Via Founderboard', render: (r) => (
          <span className={`font-mono text-xs ${r.founderboard_count ? 'text-emerald-400' : 'text-slate-600'}`} data-testid={`program-fb-${r.id}`}>
            {r.founderboard_count || 0}
            {r.imported_count > 0 && <span className="ml-1.5 text-[10px] text-slate-500" title="Imported by the VC via bulk upload">+{r.imported_count} imported</span>}
          </span>
        ) },
        { key: 'draft_count', label: 'In draft', render: (r) => (
          <span className={`font-mono text-xs ${r.draft_count ? 'text-amber-400' : 'text-slate-600'}`}>{r.draft_count || 0}</span>
        ) },
        { key: 'source', label: 'Source', render: (r) => <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-slate-800">{r.source || 'manual'}</span> },
        { key: 'created_at', label: 'Created', render: (r) => <span className="text-xs text-slate-400">{r.created_at?.slice(0, 10)}</span> },
      ]} />
    </Page>
  );
};

// ─────────────── DATA BACKUP ───────────────
// Runs backend/backup_applications.py server-side and streams the resulting zip
// back to the browser: one CSV row per application plus every uploaded document.

const BACKUP_KINDS = [
  { key: 'incubator', label: 'Incubator programs' },
  { key: 'vc',        label: 'VC watchlists' },
];

const BackupPage = ({ me }) => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState('all');           // all | incubator | vc
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);      // [] = every program in scope
  const [withDocuments, setWithDocuments] = useState(true);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);

  const superAdmin = !!me?.is_super_admin;

  useEffect(() => {
    if (!superAdmin) { setLoading(false); return; }
    api.get('/admin/backup/programs')
      .then((r) => setPrograms(r.data.programs || []))
      .catch((e) => toast.error(e.response?.data?.detail || 'Could not load programs'))
      .finally(() => setLoading(false));
  }, [superAdmin]);

  // Switching the kind filter clears a selection that no longer applies.
  useEffect(() => { setSelected([]); }, [kind]);

  const inScope = useMemo(
    () => programs.filter((p) => kind === 'all' || p.kind === kind),
    [programs, kind],
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return inScope;
    return inScope.filter((p) => `${p.name} ${p.fund_name}`.toLowerCase().includes(q));
  }, [inScope, search]);

  const toggle = (id) => setSelected((prev) => (
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
  ));

  // An empty selection means "everything in scope", so the counts below have to
  // fall back to the scope rather than reading zero.
  const effective = selected.length ? inScope.filter((p) => selected.includes(p.id)) : inScope;
  const applicationTotal = effective.reduce((sum, p) => sum + (p.application_count || 0), 0);

  const download = async () => {
    setBusy(true);
    setLast(null);
    try {
      const r = await api.get('/admin/backup/applications', {
        params: {
          ...(selected.length ? { program_ids: selected.join(',') } : {}),
          ...(kind !== 'all' ? { entity_type: kind } : {}),
          include_documents: withDocuments,
        },
        responseType: 'blob',
      });
      const fromHeader = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/.exec(r.headers['content-disposition'] || '');
      const filename = decodeURIComponent(fromHeader?.[1] || 'founderboard-applications.zip');
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);

      const stats = {
        filename,
        applications: r.headers['x-backup-applications'],
        documents: r.headers['x-backup-documents'],
        errors: Number(r.headers['x-backup-errors'] || 0),
        capped: r.headers['x-backup-size-capped'] === '1',
      };
      setLast(stats);
      toast.success(`Backup saved · ${stats.applications} applications, ${stats.documents} documents`);
      if (stats.errors > 0) toast.warning(`${stats.errors} document(s) could not be downloaded — see download_errors.csv`);
      if (stats.capped) toast.warning('Size limit reached — some documents were skipped. Narrow the filter and retry.');
    } catch (e) {
      // A blob-typed error body has to be read back as text before it's usable.
      let msg = 'Backup failed';
      try { msg = JSON.parse(await e.response?.data?.text())?.detail || msg; } catch { /* keep default */ }
      toast.error(msg);
    } finally { setBusy(false); }
  };

  if (!superAdmin) {
    return (
      <Page title="Data Backup" subtitle="Export applications and their uploaded documents." testid="backup-page">
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-start gap-2 max-w-2xl" data-testid="backup-gate">
          <Warning size={16} weight="bold" className="text-amber-400 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-200">
            Super admin access is required. A backup contains applicant contact details and every
            uploaded document across the selected programs.
          </div>
        </div>
      </Page>
    );
  }

  return (
    <Page
      title="Data Backup"
      subtitle="Download applications, answers and uploaded documents as a zip."
      testid="backup-page"
      actions={
        <button
          onClick={download}
          disabled={busy || !effective.length}
          data-testid="backup-download"
          className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-50"
        >
          {busy
            ? <><SpinnerGap size={13} weight="bold" className="animate-spin" /> Building archive…</>
            : <><DownloadSimple size={13} weight="bold" /> Download backup</>}
        </button>
      }
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatCard label="Programs in backup" value={effective.length} sub={selected.length ? `${selected.length} selected` : 'All in scope'} />
        <StatCard label="Applications" value={applicationTotal.toLocaleString('en-IN')} tone="slate" />
        <StatCard label="Incubator programs" value={programs.filter((p) => p.kind === 'incubator').length} tone="amber" />
        <StatCard label="VC watchlists" value={programs.filter((p) => p.kind === 'vc').length} tone="slate" />
      </div>

      <div className="bg-[#111827] border border-slate-800 rounded-xl p-4 mb-4 space-y-3" data-testid="backup-filters">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mr-1">Filter</span>
          {[{ key: 'all', label: 'All programs' }, ...BACKUP_KINDS].map((k) => (
            <button
              key={k.key}
              onClick={() => setKind(k.key)}
              data-testid={`backup-kind-${k.key}`}
              className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                kind === k.key
                  ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300'
                  : 'bg-[#0a0e14] border-slate-800 text-slate-400 hover:text-slate-200'
              }`}
            >
              {k.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlass size={12} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search program or workspace…"
              data-testid="backup-search"
              className="w-full bg-[#0a0e14] border border-slate-800 rounded-lg pl-8 pr-3 py-2 text-sm"
            />
          </div>
          <button onClick={() => setSelected(visible.map((p) => p.id))} data-testid="backup-select-all" className="text-xs text-slate-400 hover:text-slate-100 px-2 py-2">Select all shown</button>
          <button onClick={() => setSelected([])} data-testid="backup-clear" className="text-xs text-slate-400 hover:text-slate-100 px-2 py-2">Clear</button>
          <label className="inline-flex items-center gap-1.5 text-xs text-slate-400 pl-2 border-l border-slate-800">
            <input type="checkbox" checked={withDocuments} onChange={(e) => setWithDocuments(e.target.checked)} data-testid="backup-include-documents" />
            Include uploaded documents
          </label>
        </div>

        <div className="text-[11px] text-slate-500">
          {selected.length
            ? `${selected.length} program(s) selected.`
            : `No selection — every program in scope will be backed up (${inScope.length}).`}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading programs…</div>
      ) : (
        <Table
          testid="backup-programs-table"
          empty="No programs match this filter."
          rows={visible}
          cols={[
            {
              key: 'select',
              label: '',
              render: (r) => (
                <input
                  type="checkbox"
                  checked={selected.includes(r.id)}
                  onChange={() => toggle(r.id)}
                  data-testid={`backup-pick-${r.id}`}
                />
              ),
            },
            { key: 'name', label: 'Program' },
            {
              key: 'kind',
              label: 'Type',
              render: (r) => (
                <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded ${
                  r.kind === 'incubator' ? 'bg-amber-500/15 text-amber-400' : 'bg-slate-800 text-slate-400'
                }`}>
                  {r.kind === 'incubator' ? 'Incubator' : 'VC watchlist'}
                </span>
              ),
            },
            { key: 'fund_name', label: 'Workspace', render: (r) => <span className="text-xs text-slate-400">{r.fund_name}</span> },
            { key: 'status', label: 'Status', render: (r) => <span className={`text-xs ${r.status === 'open' ? 'text-emerald-400' : r.status === 'closed' ? 'text-slate-500' : 'text-amber-400'}`}>{r.status || '—'}</span> },
            { key: 'application_count', label: 'Apps', render: (r) => <span className="font-mono text-xs">{r.application_count}</span> },
          ]}
        />
      )}

      {last && (
        <div className="mt-4 bg-[#111827] border border-slate-800 rounded-xl p-4 text-sm" data-testid="backup-result">
          <div className="flex items-center gap-2 text-emerald-400 font-medium mb-1">
            <Check size={13} weight="bold" /> Last backup
          </div>
          <div className="text-slate-300 font-mono text-xs break-all">{last.filename}</div>
          <div className="text-[11px] text-slate-500 mt-1">
            {last.applications} application(s) · {last.documents} document(s)
            {last.errors > 0 && <span className="text-amber-400"> · {last.errors} download error(s)</span>}
          </div>
        </div>
      )}

      <div className="mt-4 text-[11px] text-slate-500 max-w-3xl leading-relaxed">
        The archive holds <span className="font-mono">founderboard_applications.csv</span> (one row per
        application, one column per program question), the uploaded documents under{' '}
        <span className="font-mono">uploaded_documents/</span>, and a{' '}
        <span className="font-mono">manifest.json</span> describing the export. Anything that could not
        be fetched from storage is listed in <span className="font-mono">download_errors.csv</span>.
        Large backups take a while to build — the download starts once the archive is complete.
      </div>
    </Page>
  );
};

const PaymentsPage = () => {
  const [data, setData] = useState({ charges: [], totals: {} });
  useEffect(() => { api.get('/admin/payments').then((r) => setData(r.data)); }, []);
  return (
    <Page title="Payments" subtitle="Every engagement billing charge on the platform." testid="payments-page">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatCard label="Paid volume (₹)" value={(data.totals.paid_total || 0).toLocaleString('en-IN')} sub={`${data.totals.count_paid || 0} charges`} />
        <StatCard label="Pending volume (₹)" value={(data.totals.pending_total || 0).toLocaleString('en-IN')} sub={`${data.totals.count_pending || 0} charges`} tone="amber" />
        <StatCard label="Total charges" value={data.charges.length} tone="slate" />
        <StatCard label="Est. platform take (10%)" value={Math.round((data.totals.paid_total || 0) * 0.1).toLocaleString('en-IN')} tone="emerald" />
      </div>
      <Table testid="payments-table" cols={[
        { key: 'label', label: 'Label' },
        { key: 'amount', label: 'Amount', render: (r) => <span className="font-mono text-xs">₹{(r.amount || 0).toLocaleString('en-IN')}</span> },
        { key: 'status', label: 'Status', render: (r) => <span className={`text-xs ${r.status === 'paid' ? 'text-emerald-400' : 'text-amber-400'}`}>{r.status}</span> },
        { key: 'founder_id', label: 'Founder', render: (r) => <span className="text-xs font-mono">{(r.founder_id || '').slice(0, 8)}</span> },
        { key: 'created_at', label: 'When', render: (r) => <span className="text-xs text-slate-400">{r.created_at?.slice(0, 10)}</span> },
      ]} rows={data.charges} empty="No charges yet." />
    </Page>
  );
};

/* ------------------------------------- Incubator / VC contract billing ----- */

// Mirrors vc_billing.add_months — calendar months, clamped to month end.
const addMonthsIso = (iso, months) => {
  if (!iso || !months) return null;
  const [y, m, d] = iso.split('-').map(Number);
  const total = m - 1 + Number(months);
  const year = y + Math.floor(total / 12);
  const month = total % 12;
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return new Date(Date.UTC(year, month, Math.min(d, lastDay))).toISOString().slice(0, 10);
};
const fmtDay = (iso) => (iso
  ? new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
  : '—');

const VC_BILLING_STATE = {
  self_serve:  { label: 'Not enabled', cls: 'bg-slate-800 text-slate-400' },
  anchor:      { label: 'Anchor',      cls: 'bg-violet-500/15 text-violet-300' },
  free_period: { label: 'Free period', cls: 'bg-sky-500/15 text-sky-300' },
  active:      { label: 'Active',      cls: 'bg-emerald-500/15 text-emerald-400' },
  payment_due: { label: 'Payment due', cls: 'bg-amber-500/15 text-amber-400' },
  grace:       { label: 'Grace',       cls: 'bg-amber-500/15 text-amber-400' },
  paused:      { label: 'Paused',      cls: 'bg-red-500/15 text-red-400' },
};

const VC_PAYMENT_STATE = {
  none:    { label: '—',          cls: 'text-slate-500' },
  not_due: { label: 'Not due yet', cls: 'bg-sky-500/15 text-sky-300' },
  due:     { label: 'Payment due', cls: 'bg-amber-500/15 text-amber-400' },
  paid:    { label: 'Paid',        cls: 'bg-emerald-500/15 text-emerald-400' },
};

const downloadInvoice = async (p) => {
  try {
    const r = await api.get(`/admin/vc-billing/invoices/${p.invoice_id}/pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url; a.download = `${p.invoice_number}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 30000);
  } catch (e) { toast.error('Invoice not available'); }
};

const contractToDraft = (c = {}) => ({
  enabled: !!c.enabled,
  agreement_date: c.agreement_date || '',
  contract_months: c.contract_months ?? '',
  free_days: c.free_days ?? '',
  free_unlimited: !!c.free_unlimited,
  price_pre_gst: c.price_pre_gst ?? '',
});

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

// Which bucket a workspace falls in. Drives the tabs at the top of the page.
const billingBucket = (f) => {
  if (f.view?.state === 'anchor') return 'free';
  return f.view?.enabled ? 'billed' : 'unbilled';
};
const needsAttention = (f) => billingBucket(f) === 'billed' && (
  f.view?.payment?.status === 'due' || ['grace', 'paused'].includes(f.view?.state) || f.view?.due_soon
);

const VC_BILLING_TABS = [
  { key: 'all',       label: 'All',             match: () => true },
  { key: 'billed',    label: 'Billed',          match: (f) => billingBucket(f) === 'billed' },
  { key: 'unbilled',  label: 'Not billed',      match: (f) => billingBucket(f) === 'unbilled' },
  { key: 'free',      label: 'Free for life',   match: (f) => billingBucket(f) === 'free' },
  { key: 'attention', label: 'Needs attention', match: needsAttention },
];

// URL-backed filter defaults — anything equal to its default is left out of the URL.
const VC_BILLING_FILTERS = { tab: 'all', q: '', type: 'all', status: 'all', pay: 'all', exp: 'all', sort: 'name' };

const VC_BILLING_SORTS = {
  name:   (a, b) => (a.fund_name || '').localeCompare(b.fund_name || ''),
  expiry: (a, b) => (a.view?.expiry_date || '9999').localeCompare(b.view?.expiry_date || '9999'),
  value:  (a, b) => (b.view?.price_pre_gst || 0) - (a.view?.price_pre_gst || 0),
  paid:   (a, b) => (b.last_paid_at || '').localeCompare(a.last_paid_at || ''),
};

const filterSelect = 'bg-[#0a0e14] border border-slate-800 rounded-lg px-2.5 py-1.5 text-xs text-slate-300 focus:outline-none focus:border-emerald-500';

const VcBillingPage = () => {
  const [rows, setRows] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());
  const [params, setParams] = useSearchParams();
  const filters = Object.fromEntries(Object.entries(VC_BILLING_FILTERS).map(([k, d]) => [k, params.get(k) ?? d]));
  const setFilter = (key, value) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    if (value === VC_BILLING_FILTERS[key]) next.delete(key); else next.set(key, value);
    return next;
  }, { replace: true });
  const filtersActive = ['q', 'type', 'status', 'pay', 'exp'].some((k) => filters[k] !== VC_BILLING_FILTERS[k]);
  const clearFilters = () => setParams((prev) => {
    const next = new URLSearchParams();
    if (prev.get('tab')) next.set('tab', prev.get('tab'));
    if (prev.get('sort')) next.set('sort', prev.get('sort'));
    return next;
  }, { replace: true });

  const load = useCallback(async () => {
    try {
      const r = await api.get('/admin/vc-billing');
      setRows(r.data.funds || []);
      setDrafts(Object.fromEntries((r.data.funds || []).map((f) => [f.fund_id, contractToDraft(f.contract)])));
    } catch (e) { toast.error(e.response?.data?.detail || 'Load failed'); setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setField = (id, patch) => setDrafts((p) => ({ ...p, [id]: { ...p[id], ...patch } }));
  const isDirty = (f) => JSON.stringify(drafts[f.fund_id]) !== JSON.stringify(contractToDraft(f.contract));
  const resetDraft = (f) => setDrafts((p) => ({ ...p, [f.fund_id]: contractToDraft(f.contract) }));
  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const save = async (f) => {
    const d = drafts[f.fund_id];
    const num = (v) => (v === '' || v === null ? null : Number(v));
    setSaving(f.fund_id);
    try {
      const r = await api.put(`/admin/vc-billing/${f.fund_id}`, {
        enabled: d.enabled,
        agreement_date: d.agreement_date || null,
        contract_months: num(d.contract_months),
        free_days: d.free_unlimited ? null : num(d.free_days),
        free_unlimited: d.free_unlimited,
        price_pre_gst: num(d.price_pre_gst),
      });
      setRows((p) => p.map((x) => (x.fund_id === f.fund_id ? { ...x, contract: r.data.contract, view: r.data.view } : x)));
      setDrafts((p) => ({ ...p, [f.fund_id]: contractToDraft(r.data.contract) }));
      toast.success(`Billing saved for ${f.fund_name || 'workspace'}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(''); }
  };

  // Everything except the tab, so the tab counts reflect the other filters.
  const baseFiltered = useMemo(() => (rows || []).filter((f) => {
    const v = f.view || {};
    if (filters.type !== 'all' && f.entity_type !== filters.type) return false;
    if (filters.status !== 'all' && v.state !== filters.status) return false;
    if (filters.pay !== 'all' && (v.payment?.status || 'none') !== filters.pay) return false;
    if (filters.exp !== 'all') {
      const d = v.days_to_expiry;
      if (d === null || d === undefined || v.state === 'anchor' || !v.enabled) return false;
      if (filters.exp === 'expired' ? d >= 0 : (d < 0 || d > Number(filters.exp))) return false;
    }
    const needle = filters.q.trim().toLowerCase();
    return !needle || `${f.fund_name} ${f.owner_email} ${f.owner_name}`.toLowerCase().includes(needle);
  }), [rows, filters.type, filters.status, filters.pay, filters.exp, filters.q]);

  const tab = VC_BILLING_TABS.find((t) => t.key === filters.tab) || VC_BILLING_TABS[0];
  const visible = useMemo(
    () => baseFiltered.filter(tab.match).sort(VC_BILLING_SORTS[filters.sort] || VC_BILLING_SORTS.name),
    [baseFiltered, tab, filters.sort],
  );

  const stats = useMemo(() => {
    const s = { collected: 0, invoices: 0, billed: 0, paid: 0, dueCount: 0, dueAmount: 0, expiring: 0, paused: 0 };
    (rows || []).forEach((f) => {
      const v = f.view || {};
      s.collected += f.collected_total || 0;
      s.invoices += f.invoice_count || 0;
      if (billingBucket(f) === 'billed') s.billed += 1;
      if (v.payment?.status === 'paid') s.paid += 1;
      if (v.payment?.status === 'due') { s.dueCount += 1; s.dueAmount += v.payment.amount || 0; }
      if (v.enabled && v.state !== 'anchor' && v.days_to_expiry >= 0 && v.days_to_expiry <= 30) s.expiring += 1;
      if (v.paused) s.paused += 1;
    });
    return s;
  }, [rows]);

  const allExpanded = visible.length > 0 && visible.every((f) => expanded.has(f.fund_id));

  return (
    <Page title="Incubator/VC Billing"
      subtitle="Negotiated contracts for VC and incubator workspaces. Self-serve signups stay unbilled on a 14-day trial. Unpaid terms pause 14 days after payment falls due; expired contracts pause 14 days after expiry."
      testid="vc-billing-page"
      tabs={(
        <div className="flex gap-1 overflow-x-auto" role="tablist" data-testid="vc-billing-tabs">
          {VC_BILLING_TABS.map((t) => {
            const n = baseFiltered.filter(t.match).length;
            const active = tab.key === t.key;
            return (
              <button key={t.key} role="tab" aria-selected={active} onClick={() => setFilter('tab', t.key)}
                data-testid={`vc-billing-tab-${t.key}`}
                className={`px-3 py-2 text-xs font-semibold border-b-2 whitespace-nowrap transition-colors ${active ? 'border-emerald-400 text-emerald-300' : 'border-transparent text-slate-400 hover:text-slate-200'}`}>
                {t.label}
                <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[10px] ${t.key === 'attention' && n > 0 ? 'bg-amber-500/15 text-amber-400' : 'bg-slate-800 text-slate-400'}`}>{n}</span>
              </button>
            );
          })}
        </div>
      )}>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
        <StatCard label="Collected" value={inr(stats.collected)} sub={`${stats.invoices} invoice${stats.invoices === 1 ? '' : 's'}`} />
        <StatCard label="Billed workspaces" value={stats.billed} sub={`${stats.paid} paid for current term`} tone="slate" />
        <StatCard label="Payment due" value={stats.dueCount} sub={stats.dueCount ? `${inr(stats.dueAmount)} outstanding` : 'nothing outstanding'} tone="amber" />
        <StatCard label="Expiring ≤ 30 days" value={stats.expiring} tone="amber" />
        <StatCard label="Paused" value={stats.paused} tone="red" />
      </div>

      <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="vc-billing-filters">
        <div className="relative">
          <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input value={filters.q} onChange={(e) => setFilter('q', e.target.value)} placeholder="Search name or owner email"
            data-testid="vc-billing-search" className="bg-[#0a0e14] border border-slate-800 rounded-lg pl-7 pr-3 py-1.5 text-xs w-60 focus:outline-none focus:border-emerald-500" />
        </div>
        <select value={filters.type} onChange={(e) => setFilter('type', e.target.value)} className={filterSelect} data-testid="vc-billing-filter-type" aria-label="Type">
          <option value="all">All types</option><option value="incubator">Incubators</option><option value="vc">VCs</option>
        </select>
        <select value={filters.status} onChange={(e) => setFilter('status', e.target.value)} className={filterSelect} data-testid="vc-billing-filter-status" aria-label="Status">
          <option value="all">Any status</option>
          {Object.entries(VC_BILLING_STATE).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
        </select>
        <select value={filters.pay} onChange={(e) => setFilter('pay', e.target.value)} className={filterSelect} data-testid="vc-billing-filter-payment" aria-label="Payment">
          <option value="all">Any payment</option><option value="paid">Paid</option><option value="due">Payment due</option>
          <option value="not_due">Not due yet</option><option value="none">Nothing to collect</option>
        </select>
        <select value={filters.exp} onChange={(e) => setFilter('exp', e.target.value)} className={filterSelect} data-testid="vc-billing-filter-expiry" aria-label="Expiry">
          <option value="all">Any expiry</option><option value="30">Expires ≤ 30 days</option><option value="60">Expires ≤ 60 days</option>
          <option value="90">Expires ≤ 90 days</option><option value="expired">Already expired</option>
        </select>
        <select value={filters.sort} onChange={(e) => setFilter('sort', e.target.value)} className={filterSelect} data-testid="vc-billing-sort" aria-label="Sort">
          <option value="name">Sort: Name A–Z</option><option value="expiry">Sort: Expiry soonest</option>
          <option value="value">Sort: Contract value</option><option value="paid">Sort: Recently paid</option>
        </select>
        {filtersActive && (
          <button onClick={clearFilters} data-testid="vc-billing-clear" className="text-xs text-slate-400 hover:text-slate-100 inline-flex items-center gap-1">
            <X size={11} weight="bold" /> Clear filters
          </button>
        )}
        <div className="ml-auto flex items-center gap-3 text-xs text-slate-500">
          <span data-testid="vc-billing-count">{visible.length} of {(rows || []).length}</span>
          {visible.length > 0 && (
            <button onClick={() => setExpanded(allExpanded ? new Set() : new Set(visible.map((f) => f.fund_id)))}
              data-testid="vc-billing-expand-all" className="text-slate-400 hover:text-slate-100">
              {allExpanded ? 'Collapse all' : 'Expand all'}
            </button>
          )}
        </div>
      </div>

      {rows === null ? <div className="text-sm text-slate-500">Loading…</div> : (
        <Table testid="vc-billing-table" empty="No workspaces match these filters."
          renderExpanded={(f) => (expanded.has(f.fund_id) ? (
            <VcBillingDrawer key={f.fund_id} f={f} draft={drafts[f.fund_id] || {}}
              setField={(patch) => setField(f.fund_id, patch)} dirty={isDirty(f)}
              saving={saving === f.fund_id} onSave={() => save(f)} onReset={() => resetDraft(f)} />
          ) : null)}
          cols={[
            { key: 'fund', label: 'Workspace', render: (f) => (
              <button onClick={() => toggle(f.fund_id)} aria-expanded={expanded.has(f.fund_id)}
                data-testid={`vc-billing-row-${f.fund_id}`} className="flex items-start gap-2 text-left min-w-[200px] group">
                {expanded.has(f.fund_id)
                  ? <CaretDown size={12} weight="bold" className="mt-1 text-emerald-400 flex-shrink-0" />
                  : <CaretRight size={12} weight="bold" className="mt-1 text-slate-500 group-hover:text-slate-300 flex-shrink-0" />}
                <span className="min-w-0">
                  <span className="flex items-center gap-1.5">
                    <span className="font-medium text-slate-100 truncate">{f.fund_name || '—'}</span>
                    <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded ${f.entity_type === 'incubator' ? 'bg-amber-500/15 text-amber-400' : 'bg-sky-500/15 text-sky-300'}`}>{f.entity_type === 'incubator' ? 'Incubator' : 'VC'}</span>
                  </span>
                  <span className="block text-[11px] text-slate-500 truncate">{f.owner_email || 'no owner'}</span>
                </span>
              </button>
            ) },
            { key: 'state', label: 'Status', render: (f) => {
              const s = VC_BILLING_STATE[f.view?.state] || VC_BILLING_STATE.self_serve;
              const pauseDate = f.view?.payment?.pause_date;
              return (
                <div data-testid={`vc-billing-state-${f.fund_id}`}>
                  <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${s.cls}`}>{s.label}</span>
                  {f.view?.state === 'paused' && (
                    <div className="text-[10px] text-slate-500 mt-0.5">{f.view.pause_reason === 'unpaid' ? 'unpaid' : 'contract expired'}</div>
                  )}
                  {f.view?.state === 'payment_due' && pauseDate && (
                    <div className="text-[10px] text-slate-500 mt-0.5">pauses {fmtDay(pauseDate)}</div>
                  )}
                  {f.view?.state === 'active' && f.view?.due_soon && (
                    <div className="text-[10px] text-amber-400 mt-0.5">renews in {f.view.days_to_expiry}d</div>
                  )}
                </div>
              );
            } },
            { key: 'contract', label: 'Contract', render: (f) => {
              const v = f.view || {};
              if (!v.enabled) return <span className="text-xs text-slate-500">Billing off</span>;
              return (
                <div className="text-xs">
                  <div className="font-mono text-slate-300">{fmtDay(v.agreement_date)} → {fmtDay(v.expiry_date)}</div>
                  <div className="text-[10px] text-slate-500">
                    {v.contract_months} mo · {v.free_unlimited ? 'free for life' : (v.free_days ? `${v.free_days} days free` : 'no free period')}
                  </div>
                </div>
              );
            } },
            { key: 'value', label: 'Value', render: (f) => {
              const v = f.view || {};
              if (!v.enabled || v.free_unlimited || !v.price_pre_gst) return <span className="text-xs text-slate-500">—</span>;
              return (
                <div className="text-xs">
                  <div className="font-mono text-slate-200">{inr(v.price_pre_gst)}</div>
                  <div className="text-[10px] text-slate-500">{inr(v.total_with_gst)} incl. GST</div>
                </div>
              );
            } },
            { key: 'payment', label: 'Current term', render: (f) => {
              const p = f.view?.payment || { status: 'none' };
              const m = VC_PAYMENT_STATE[p.status] || VC_PAYMENT_STATE.none;
              return (
                <div className="min-w-[120px]" data-testid={`vc-billing-payment-${f.fund_id}`} data-status={p.status}>
                  <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${m.cls}`}>{m.label}</span>
                  {p.status === 'paid' && (
                    <button onClick={() => downloadInvoice(p)} disabled={!p.invoice_id}
                      data-testid={`vc-billing-invoice-${f.fund_id}`} title="Download invoice PDF"
                      className="mt-1 flex items-center gap-1 text-[11px] font-mono text-emerald-300 hover:text-emerald-200">
                      <DownloadSimple size={10} weight="bold" /> {p.invoice_number || 'issuing…'}
                    </button>
                  )}
                </div>
              );
            } },
            { key: 'collected', label: 'Collected', render: (f) => (
              <div className="text-xs" data-testid={`vc-billing-collected-${f.fund_id}`}>
                <div className="font-mono text-slate-200">{f.collected_total ? inr(f.collected_total) : '—'}</div>
                {f.invoice_count > 0 && (
                  <div className="text-[10px] text-slate-500">{f.invoice_count} invoice{f.invoice_count === 1 ? '' : 's'} · last {fmtDay(f.last_paid_at?.slice(0, 10))}</div>
                )}
              </div>
            ) },
            { key: 'manage', label: '', render: (f) => (
              <button onClick={() => toggle(f.fund_id)} data-testid={`vc-billing-manage-${f.fund_id}`}
                className={`text-xs px-3 py-1.5 rounded-lg border ${expanded.has(f.fund_id) ? 'border-emerald-500/50 text-emerald-300' : 'border-slate-700 text-slate-300 hover:border-slate-500'}`}>
                {expanded.has(f.fund_id) ? 'Close' : 'Manage'}
                {isDirty(f) && <span className="ml-1 text-amber-400" title="Unsaved changes">•</span>}
              </button>
            ) },
          ]} rows={visible} />
      )}
    </Page>
  );
};

// Expanded row: edit the contract on the left, invoice history on the right.
const VcBillingDrawer = ({ f, draft: d, setField, dirty, saving, onSave, onReset }) => {
  const expiry = addMonthsIso(d.agreement_date, d.contract_months);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4" data-testid={`vc-billing-drawer-${f.fund_id}`}>
      <div className="lg:col-span-3 bg-[#111827] border border-slate-800 rounded-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Contract</div>
          <button type="button" role="switch" aria-checked={!!d.enabled} aria-label={`Billing for ${f.fund_name || 'workspace'}`}
            onClick={() => setField({ enabled: !d.enabled })} data-testid={`vc-billing-enabled-${f.fund_id}`}
            className="inline-flex items-center gap-2 text-xs">
            <span className={d.enabled ? 'text-emerald-400 font-semibold' : 'text-slate-500'}>Billing {d.enabled ? 'on' : 'off'}</span>
            <span className={`relative inline-block w-9 h-5 rounded-full transition-colors ${d.enabled ? 'bg-emerald-500' : 'bg-slate-700'}`}>
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${d.enabled ? 'translate-x-4' : ''}`} />
            </span>
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="Date of agreement">
            <input type="date" value={d.agreement_date || ''} data-testid={`vc-billing-agreement-${f.fund_id}`}
              onChange={(e) => setField({ agreement_date: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Contract (months)">
            <input type="number" min="1" max="120" step="1" value={d.contract_months ?? ''} placeholder="12"
              data-testid={`vc-billing-months-${f.fund_id}`}
              onChange={(e) => setField({ contract_months: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Free period (days)">
            <div className="flex items-center gap-1">
              <input type="number" min="0" step="1" value={d.free_unlimited ? '' : (d.free_days ?? '')} disabled={d.free_unlimited}
                placeholder={d.free_unlimited ? '∞' : '0'} data-testid={`vc-billing-free-${f.fund_id}`}
                onChange={(e) => setField({ free_days: e.target.value })} className={inputCls} />
              <button type="button" onClick={() => setField({ free_unlimited: !d.free_unlimited })}
                data-testid={`vc-billing-infinity-${f.fund_id}`} aria-pressed={!!d.free_unlimited}
                title="Free for life — anchor partner, never charged or paused"
                className={`p-2 rounded-lg border flex-shrink-0 ${d.free_unlimited ? 'border-violet-500/50 bg-violet-500/15 text-violet-300' : 'border-slate-800 text-slate-500 hover:text-slate-200'}`}>
                <InfinityIcon size={13} weight="bold" />
              </button>
            </div>
          </Field>
          <Field label="Final pricing pre-GST (₹)">
            <input type="number" min="0" step="1" value={d.price_pre_gst ?? ''} placeholder="0"
              data-testid={`vc-billing-price-${f.fund_id}`}
              onChange={(e) => setField({ price_pre_gst: e.target.value })} className={`${inputCls} font-mono`} />
          </Field>
        </div>
        <div className="flex items-center justify-between gap-3 mt-4 flex-wrap">
          <div className="text-xs text-slate-400">
            Contract expires <span className="font-mono text-slate-200" data-testid={`vc-billing-expiry-${f.fund_id}`}>{fmtDay(expiry)}</span>
            {d.price_pre_gst !== '' && !d.free_unlimited && Number(d.price_pre_gst) > 0 && (
              <> · {inr(Number(d.price_pre_gst) * 1.18)} incl. 18% GST</>
            )}
          </div>
          <div className="flex gap-2">
            {dirty && (
              <button onClick={onReset} className="text-xs px-3 py-1.5 border border-slate-700 rounded-lg text-slate-300">Discard</button>
            )}
            <button onClick={onSave} disabled={!dirty || saving} data-testid={`vc-billing-save-${f.fund_id}`}
              className="text-xs px-3 py-1.5 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold rounded-lg disabled:opacity-30 disabled:cursor-not-allowed">
              {saving ? 'Saving…' : 'Save contract'}
            </button>
          </div>
        </div>
      </div>
      <VcInvoiceHistory fundId={f.fund_id} refreshKey={f.view?.payment?.invoice_number} />
    </div>
  );
};

const VcInvoiceHistory = ({ fundId, refreshKey }) => {
  const [invoices, setInvoices] = useState(null);
  const [open, setOpen] = useState(true);
  useEffect(() => {
    api.get(`/admin/vc-billing/${fundId}/invoices`)
      .then((r) => setInvoices(r.data.invoices || []))
      .catch(() => setInvoices([]));
  }, [fundId, refreshKey]);
  const total = (invoices || []).reduce((sum, inv) => sum + (inv.total || 0), 0);
  return (
    <div className="lg:col-span-2 bg-[#111827] border border-slate-800 rounded-xl p-4" data-testid={`vc-billing-history-${fundId}`}>
      <button onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid={`vc-billing-history-toggle-${fundId}`}
        className="w-full flex items-center justify-between text-left">
        <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-500">
          {open ? <CaretDown size={10} weight="bold" /> : <CaretRight size={10} weight="bold" />}
          Invoice history {invoices && <span className="text-slate-600">({invoices.length})</span>}
        </span>
        {invoices?.length > 0 && <span className="text-xs font-mono text-slate-300">{inr(total)}</span>}
      </button>
      {open && (
        <div className="mt-3 space-y-1.5">
          {invoices === null ? <div className="text-xs text-slate-500">Loading…</div>
            : invoices.length === 0 ? <div className="text-xs text-slate-500">No invoices yet.</div>
            : invoices.map((inv) => (
              <div key={inv.id} className="flex items-center gap-2 bg-[#0a0e14] border border-slate-800 rounded-lg px-3 py-2"
                data-testid={`vc-billing-history-row-${inv.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-mono text-slate-100">{inv.number}</div>
                  <div className="text-[10px] text-slate-500 truncate">
                    {fmtDay(inv.period_start)} – {fmtDay(inv.period_end)} · paid {fmtDay(inv.paid_at?.slice(0, 10))}
                  </div>
                </div>
                <div className="text-xs font-mono text-slate-200">{inr(inv.total)}</div>
                <button onClick={() => downloadInvoice({ invoice_id: inv.id, invoice_number: inv.number })}
                  title="Download PDF" aria-label={`Download ${inv.number}`}
                  data-testid={`vc-billing-history-download-${inv.id}`}
                  className="p-1.5 rounded text-slate-400 hover:text-emerald-300 hover:bg-slate-800">
                  <DownloadSimple size={12} weight="bold" />
                </button>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------- Founderboard Coins ----- */

const coinsFmt = (n) => Number(n || 0).toLocaleString('en-IN');
const COIN_KIND = {
  grant:  { label: 'Grant',  cls: 'text-emerald-400' },
  earn:   { label: 'Earned', cls: 'text-emerald-400' },
  deduct: { label: 'Deduct', cls: 'text-red-400' },
  redeem: { label: 'Redeem', cls: 'text-amber-400' },
};
const COINS_FILTERS = { q: '', hold: 'all', sort: 'balance' };
const COINS_SORTS = {
  balance:  (a, b) => b.balance - a.balance,
  name:     (a, b) => (a.startup_name || '').localeCompare(b.startup_name || ''),
  activity: (a, b) => (b.last_activity_at || '').localeCompare(a.last_activity_at || ''),
};

const CoinsAdminPage = () => {
  const [wallets, setWallets] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [adjusting, setAdjusting] = useState(null);     // wallet row
  const [refreshTick, setRefreshTick] = useState(0);
  const [params, setParams] = useSearchParams();
  const filters = Object.fromEntries(Object.entries(COINS_FILTERS).map(([k, d]) => [k, params.get(k) ?? d]));
  const setFilter = (key, value) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    if (value === COINS_FILTERS[key]) next.delete(key); else next.set(key, value);
    return next;
  }, { replace: true });

  const load = useCallback(async () => {
    try { const r = await api.get('/admin/coins'); setWallets(r.data.wallets || []); }
    catch (e) { toast.error(e.response?.data?.detail || 'Load failed'); setWallets([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const visible = useMemo(() => (wallets || []).filter((w) => {
    if (filters.hold === 'holding' && w.balance <= 0) return false;
    if (filters.hold === 'empty' && w.balance > 0) return false;
    if (filters.hold === 'active' && !w.transaction_count) return false;
    const needle = filters.q.trim().toLowerCase();
    return !needle || `${w.startup_name} ${w.owner_email} ${w.owner_name}`.toLowerCase().includes(needle);
  }).sort(COINS_SORTS[filters.sort] || COINS_SORTS.balance), [wallets, filters.hold, filters.q, filters.sort]);

  const stats = useMemo(() => (wallets || []).reduce((s, w) => ({
    circulation: s.circulation + w.balance,
    granted: s.granted + w.earned,
    used: s.used + w.spent,
    holders: s.holders + (w.balance > 0 ? 1 : 0),
  }), { circulation: 0, granted: 0, used: 0, holders: 0 }), [wallets]);

  const onAdjusted = (startupId, balance) => {
    setWallets((prev) => prev.map((w) => (w.startup_id === startupId ? { ...w, balance } : w)));
    setAdjusting(null);
    setRefreshTick((t) => t + 1);
    load();
  };

  return (
    <Page title="Founderboard Coins"
      subtitle="Coin wallets for startup workspaces. For now coins move only through admin grants and deductions — every movement is logged with a reason the startup can see."
      testid="coins-admin-page">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatCard label="In circulation" value={coinsFmt(stats.circulation)} sub="coins held right now" />
        <StatCard label="Total granted" value={coinsFmt(stats.granted)} tone="slate" />
        <StatCard label="Total used / deducted" value={coinsFmt(stats.used)} tone="amber" />
        <StatCard label="Startups holding coins" value={stats.holders} sub={`of ${(wallets || []).length}`} tone="slate" />
      </div>
      <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="coins-filters">
        <div className="relative">
          <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input value={filters.q} onChange={(e) => setFilter('q', e.target.value)} placeholder="Search startup or owner email"
            data-testid="coins-search" className="bg-[#0a0e14] border border-slate-800 rounded-lg pl-7 pr-3 py-1.5 text-xs w-64 focus:outline-none focus:border-emerald-500" />
        </div>
        <select value={filters.hold} onChange={(e) => setFilter('hold', e.target.value)} className={filterSelect} data-testid="coins-filter-hold" aria-label="Balance">
          <option value="all">All startups</option><option value="holding">Holding coins</option>
          <option value="empty">Zero balance</option><option value="active">Any coin activity</option>
        </select>
        <select value={filters.sort} onChange={(e) => setFilter('sort', e.target.value)} className={filterSelect} data-testid="coins-sort" aria-label="Sort">
          <option value="balance">Sort: Highest balance</option><option value="name">Sort: Name A–Z</option><option value="activity">Sort: Recent activity</option>
        </select>
        <span className="ml-auto text-xs text-slate-500" data-testid="coins-count">{visible.length} of {(wallets || []).length}</span>
      </div>
      {wallets === null ? <div className="text-sm text-slate-500">Loading…</div> : (
        <Table testid="coins-table" empty="No startups match."
          renderExpanded={(w) => (expanded.has(w.startup_id)
            ? <CoinLedger key={w.startup_id} startupId={w.startup_id} refreshKey={refreshTick} /> : null)}
          cols={[
            { key: 'startup', label: 'Startup', render: (w) => (
              <button onClick={() => toggle(w.startup_id)} aria-expanded={expanded.has(w.startup_id)}
                data-testid={`coins-row-${w.startup_id}`} className="flex items-start gap-2 text-left min-w-[200px] group">
                {expanded.has(w.startup_id)
                  ? <CaretDown size={12} weight="bold" className="mt-1 text-emerald-400 flex-shrink-0" />
                  : <CaretRight size={12} weight="bold" className="mt-1 text-slate-500 group-hover:text-slate-300 flex-shrink-0" />}
                <span className="min-w-0">
                  <span className="block font-medium text-slate-100 truncate">{w.startup_name || '—'}</span>
                  <span className="block text-[11px] text-slate-500 truncate">{w.owner_email || 'no owner'}</span>
                </span>
              </button>
            ) },
            { key: 'balance', label: 'Balance', render: (w) => (
              <span className={`font-mono text-sm font-bold ${w.balance > 0 ? 'text-amber-300' : 'text-slate-500'}`} data-testid={`coins-balance-${w.startup_id}`}>{coinsFmt(w.balance)}</span>
            ) },
            { key: 'earned', label: 'Granted', render: (w) => <span className="font-mono text-xs text-slate-300">{coinsFmt(w.earned)}</span> },
            { key: 'spent', label: 'Used', render: (w) => <span className="font-mono text-xs text-slate-300">{coinsFmt(w.spent)}</span> },
            { key: 'activity', label: 'Last activity', render: (w) => (
              <span className="text-xs text-slate-400">{w.last_activity_at ? `${fmtDay(w.last_activity_at.slice(0, 10))} · ${w.transaction_count} txn${w.transaction_count === 1 ? '' : 's'}` : '—'}</span>
            ) },
            { key: 'actions', label: '', render: (w) => (
              <button onClick={() => setAdjusting(w)} data-testid={`coins-adjust-${w.startup_id}`}
                className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-slate-700 text-slate-200 hover:border-emerald-500/60 hover:text-emerald-300">
                <FounderboardCoinIcon size={13} /> Adjust
              </button>
            ) },
          ]} rows={visible} />
      )}
      {adjusting && <CoinAdjustModal wallet={adjusting} onClose={() => setAdjusting(null)} onDone={onAdjusted} />}
    </Page>
  );
};

const CoinAdjustModal = ({ wallet, onClose, onDone }) => {
  const [form, setForm] = useState({ direction: 'grant', amount: '', reason: '', note: '' });
  const [busy, setBusy] = useState(false);
  const amount = parseInt(form.amount, 10);
  const valid = Number.isInteger(amount) && amount > 0 && form.reason.trim()
    && !(form.direction === 'deduct' && amount > wallet.balance);
  const after = wallet.balance + (Number.isInteger(amount) ? (form.direction === 'grant' ? amount : -amount) : 0);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post(`/admin/coins/${wallet.startup_id}/adjust`, {
        direction: form.direction, amount, reason: form.reason.trim(), note: form.note.trim() || null,
      });
      toast.success(`${form.direction === 'grant' ? 'Granted' : 'Deducted'} ${coinsFmt(amount)} coins — ${wallet.startup_name}`);
      onDone(wallet.startup_id, r.data.balance);
    } catch (err) { toast.error(err.response?.data?.detail || 'Adjustment failed'); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={`Adjust coins — ${wallet.startup_name || 'startup'}`} subtitle={`Current balance: ${coinsFmt(wallet.balance)} coins`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="coins-adjust-form">
        <div className="grid grid-cols-2 gap-2">
          {[['grant', 'Grant coins'], ['deduct', 'Deduct coins']].map(([k, label]) => (
            <button key={k} type="button" onClick={() => setForm((p) => ({ ...p, direction: k }))}
              data-testid={`coins-direction-${k}`} aria-pressed={form.direction === k}
              className={`text-xs py-2 rounded-lg border font-semibold ${form.direction === k
                ? (k === 'grant' ? 'border-emerald-500/60 bg-emerald-500/10 text-emerald-300' : 'border-red-500/60 bg-red-500/10 text-red-300')
                : 'border-slate-800 text-slate-400'}`}>
              {label}
            </button>
          ))}
        </div>
        <Field label="Amount (coins)" hint={form.direction === 'deduct' && amount > wallet.balance ? 'More than the current balance.' : undefined}>
          <input type="number" min="1" step="1" required value={form.amount} autoFocus
            onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} data-testid="coins-amount" className={`${inputCls} font-mono`} />
        </Field>
        <Field label="Reason" hint="Shown to the startup in their coin history.">
          <input required maxLength={200} value={form.reason} placeholder="e.g. Welcome bonus, Cohort reward, Correction"
            onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))} data-testid="coins-reason" className={inputCls} />
        </Field>
        <Field label="Internal note (optional)">
          <input maxLength={500} value={form.note}
            onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))} data-testid="coins-note" className={inputCls} />
        </Field>
        <div className="flex items-center justify-between pt-1">
          <span className="text-xs text-slate-500">New balance: <span className="font-mono text-slate-200">{coinsFmt(Math.max(0, after))}</span></span>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="text-xs px-3 py-1.5 border border-slate-700 rounded-lg">Cancel</button>
            <button type="submit" disabled={!valid || busy} data-testid="coins-submit"
              className={`text-xs px-3 py-1.5 font-semibold rounded-lg disabled:opacity-30 ${form.direction === 'grant' ? 'bg-emerald-500 text-black' : 'bg-red-500 text-white'}`}>
              {busy ? 'Saving…' : (form.direction === 'grant' ? 'Grant' : 'Deduct')}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

const CoinLedger = ({ startupId, refreshKey }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(`/admin/coins/${startupId}/transactions`)
      .then((r) => setRows(r.data.transactions || []))
      .catch(() => setRows([]));
  }, [startupId, refreshKey]);
  if (rows === null) return <div className="text-xs text-slate-500">Loading history…</div>;
  if (rows.length === 0) return <div className="text-xs text-slate-500" data-testid={`coins-ledger-${startupId}`}>No coin activity yet.</div>;
  return (
    <div className="space-y-1.5" data-testid={`coins-ledger-${startupId}`}>
      {rows.map((t) => {
        const k = COIN_KIND[t.kind] || { label: t.kind, cls: 'text-slate-400' };
        return (
          <div key={t.id} className="flex items-center gap-3 bg-[#111827] border border-slate-800 rounded-lg px-3 py-2">
            <span className={`text-[10px] uppercase font-semibold w-14 ${k.cls}`}>{k.label}</span>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-slate-100 truncate">{t.reason || '—'}</div>
              <div className="text-[10px] text-slate-500 truncate">
                {new Date(t.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                {t.actor_email && <> · by {t.actor_email}</>}
                {t.note && <> · {t.note}</>}
              </div>
            </div>
            <span className={`font-mono text-xs font-bold ${t.amount > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {t.amount > 0 ? '+' : '−'}{coinsFmt(Math.abs(t.amount))}
            </span>
            <span className="font-mono text-[10px] text-slate-500 w-20 text-right">bal {coinsFmt(t.balance_after)}</span>
          </div>
        );
      })}
    </div>
  );
};

/* ------------------------------------------------- Credit Desk (Iter 69) ----- */

/**
 * The credit team's queue. Founders submit one application; this is where it's
 * read, moved through the pipeline, and answered.
 *
 * Layout mirrors the founder and vendor screens: queue left, the selected
 * application and its thread right. The thread is the same ServiceChat the
 * founder sees, pointed at /credit/applications — so both sides render the same
 * component and can't drift.
 */
const CREDIT_STATUS_TONE = {
  submitted:      'text-blue-400',
  under_review:   'text-blue-400',
  info_requested: 'text-amber-400',
  approved:       'text-emerald-400',
  declined:       'text-slate-400',
  closed:         'text-slate-500',
};

const CreditDeskPage = () => {
  const [data, setData] = useState({ applications: [], counts: {}, statuses: [] });
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [checked, setChecked] = useState(() => new Set());   // xlsx export selection
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/admin/credit/applications', { params: filter ? { status: filter } : {} });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load credit applications'); }
    finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  /**
   * `keepStale` refreshes the panel WITHOUT blanking it first.
   *
   * This is the fix for the reload loop: ServiceChat calls onRead on every fetch
   * (including its 20s poll). If that handler blanked `detail`, the panel fell
   * back to its empty state, which unmounted ServiceChat, which remounted and
   * fetched again — forever. Only a genuine switch to a different application
   * clears the panel now.
   */
  const openDetail = useCallback(async (id, { keepStale = false } = {}) => {
    setSelectedId(id);
    if (!keepStale) setDetail(null);
    try {
      const r = await api.get(`/admin/credit/applications/${id}`);
      setDetail(r.data.application);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load application'); }
  }, []);

  // What ServiceChat's onRead is allowed to do: clear the badge in the list, and
  // nothing else. Returns the same object when there's nothing to clear so a
  // poll tick can't trigger a re-render cascade.
  const clearUnread = useCallback((id) => {
    setData((prev) => {
      if (!(prev.applications || []).some((a) => a.id === id && (a.chat?.unread || 0) > 0)) return prev;
      return {
        ...prev,
        unread_messages: Math.max(0, (prev.unread_messages || 0)
          - ((prev.applications.find((a) => a.id === id)?.chat?.unread) || 0)),
        applications: prev.applications.map((a) =>
          a.id === id ? { ...a, chat: { ...a.chat, unread: 0 } } : a),
      };
    });
  }, []);

  const rows = data.applications || [];
  const allChecked = rows.length > 0 && rows.every((r) => checked.has(r.id));
  const toggleAll = () => setChecked(allChecked ? new Set() : new Set(rows.map((r) => r.id)));
  const toggleOne = (id) => setChecked((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const exportXlsx = async (ids) => {
    setExporting(true);
    try {
      const r = await api.post('/admin/credit/applications/export',
        { ids: ids && ids.length ? ids : null }, { responseType: 'blob' });
      // Filename comes off Content-Disposition so the server owns the naming
      // (it differs for a single application vs a batch).
      const cd = r.headers['content-disposition'] || '';
      const match = /filename="?([^"]+)"?/.exec(cd);
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = match ? match[1] : 'credit-applications.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
      toast.success(ids?.length ? `Exported ${ids.length} application${ids.length === 1 ? '' : 's'}.` : 'Exported all applications.');
    } catch (e) {
      // A blob-typed error body has to be read back as text before the detail
      // is legible.
      let detail = 'Export failed';
      try { detail = JSON.parse(await e.response.data.text()).detail || detail; } catch (_) { /* keep default */ }
      toast.error(detail);
    } finally { setExporting(false); }
  };

  const pending = ['submitted', 'under_review', 'info_requested']
    .reduce((s, k) => s + (data.counts?.[k] || 0), 0);

  return (
    <Page title="Credit Desk" subtitle="Founder credit applications, and the thread with each one."
      testid="credit-desk-page"
      actions={<div className="flex items-center gap-2">
        <button onClick={() => exportXlsx([...checked])} disabled={exporting || checked.size === 0}
          data-testid="credit-export-selected"
          className="text-xs px-2.5 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 text-white inline-flex items-center gap-1.5 disabled:opacity-40">
          <DownloadSimple size={12} /> {exporting ? 'Preparing…' : `Export selected${checked.size ? ` (${checked.size})` : ''}`}
        </button>
        <button onClick={() => exportXlsx(null)} disabled={exporting}
          data-testid="credit-export-all"
          className="text-xs px-2.5 py-1.5 rounded bg-slate-800 hover:bg-slate-700 inline-flex items-center gap-1.5 disabled:opacity-40">
          <DownloadSimple size={12} /> Export all
        </button>
        <button onClick={load} className="text-xs px-2.5 py-1.5 rounded bg-slate-800 hover:bg-slate-700 inline-flex items-center gap-1.5">
          <ArrowClockwise size={12} /> Refresh
        </button>
      </div>}>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatCard label="Awaiting action" value={pending} sub="submitted / in review / info requested" tone="amber" />
        <StatCard label="Approved" value={data.counts?.approved || 0} tone="emerald" />
        <StatCard label="Unread messages" value={data.unread_messages || 0} tone={data.unread_messages ? 'amber' : 'slate'} />
        <StatCard label="Total" value={data.applications?.length || 0} tone="slate" />
      </div>

      <div className="flex items-center gap-1.5 mb-3 flex-wrap">
        <button onClick={() => setFilter('')}
          className={`text-[11px] px-2 py-1 rounded ${!filter ? 'bg-emerald-600 text-white' : 'bg-slate-800 text-slate-300'}`}>
          All
        </button>
        {(data.statuses || []).filter((s) => s !== 'draft').map((s) => (
          <button key={s} onClick={() => setFilter(s)}
            className={`text-[11px] px-2 py-1 rounded ${filter === s ? 'bg-emerald-600 text-white' : 'bg-slate-800 text-slate-300'}`}>
            {s.replace('_', ' ')} {data.counts?.[s] ? `(${data.counts[s]})` : ''}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-slate-400 py-8 text-center">Loading…</div>
      ) : (
        <div className="flex flex-col xl:flex-row gap-4 items-start">
          <div className="w-full min-w-0 xl:flex-[60_1_0%]">
            <Table testid="credit-applications-table" cols={[
              { key: '_sel',
                label: <input type="checkbox" checked={allChecked} onChange={toggleAll}
                  aria-label="Select all" data-testid="credit-select-all" />,
                render: (r) => <input type="checkbox" checked={checked.has(r.id)}
                  onChange={() => toggleOne(r.id)} aria-label={`Select ${r.reference || r.startup_name}`}
                  data-testid={`credit-select-${r.id}`} /> },
              { key: 'reference', label: 'Ref', render: (r) => <span className="font-mono text-xs">{r.reference || '—'}</span> },
              { key: 'startup_name', label: 'Startup' },
              { key: 'amount_sought_inr', label: 'Sought', render: (r) => <span className="font-mono text-xs">₹{Number(r.amount_sought_inr || 0).toLocaleString('en-IN')}</span> },
              { key: 'credit_types', label: 'Types', render: (r) => <span className="text-xs text-slate-400">{(r.credit_types || []).length}</span> },
              { key: 'status', label: 'Status', render: (r) => <span className={`text-xs ${CREDIT_STATUS_TONE[r.status] || 'text-slate-400'}`}>{(r.status || '').replace('_', ' ')}</span> },
              { key: 'chat', label: '', render: (r) => (r.chat?.unread
                ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 inline-flex items-center gap-1"><ChatCircleDots size={9} weight="fill" /> {r.chat.unread}</span>
                : null) },
              { key: 'submitted_at', label: 'Submitted', render: (r) => <span className="text-xs text-slate-400">{r.submitted_at?.slice(0, 10) || '—'}</span> },
              { key: 'actions', label: '', render: (r) => (
                <button onClick={() => openDetail(r.id)}
                  data-testid={`credit-open-${r.id}`}
                  className={`text-[11px] px-2 py-1 rounded ${selectedId === r.id ? 'bg-emerald-600 text-white' : 'bg-slate-800 hover:bg-slate-700'}`}>
                  {selectedId === r.id ? 'Open' : 'Review'}
                </button>
              ) },
            ]} rows={rows} empty="No credit applications yet." />
          </div>

          <div className="w-full min-w-0 xl:flex-[40_1_0%] xl:sticky xl:top-4">
            <CreditDetailPanel app={detail} loading={!!selectedId && !detail}
              onSaved={() => { load(); openDetail(selectedId, { keepStale: true }); }}
              onChatRead={() => selectedId && clearUnread(selectedId)}
              onExport={() => selectedId && exportXlsx([selectedId])}
              exporting={exporting}
              onClose={() => { setSelectedId(null); setDetail(null); }} />
          </div>
        </div>
      )}
    </Page>
  );
};

const CreditDetailPanel = ({ app, loading, onSaved, onChatRead, onExport, exporting, onClose }) => {
  const [status, setStatus] = useState('');
  const [decisionNote, setDecisionNote] = useState('');
  const [internal, setInternal] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setStatus(app?.status || '');
    setDecisionNote(app?.decision_note || '');
    setInternal(app?.internal_notes || '');
  }, [app?.id, app?.status, app?.decision_note, app?.internal_notes]);

  if (loading) {
    return <div className="bg-[#0d1219] border border-slate-800 rounded-lg p-6 text-sm text-slate-400">Loading application…</div>;
  }
  if (!app) {
    return (
      <div className="bg-[#0d1219] border border-slate-800 rounded-lg p-6 text-center" data-testid="credit-detail-empty">
        <Bank size={22} className="mx-auto mb-2 text-slate-600" />
        <div className="text-sm text-slate-300 font-medium">No application selected</div>
        <p className="text-[11px] text-slate-500 mt-1">Pick one on the left to read it and reply to the founder.</p>
      </div>
    );
  }

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/credit/applications/${app.id}`, {
        status: status !== app.status ? status : undefined,
        decision_note: decisionNote !== (app.decision_note || '') ? decisionNote : undefined,
        internal_notes: internal !== (app.internal_notes || '') ? internal : undefined,
      });
      toast.success('Application updated.');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
    finally { setBusy(false); }
  };

  const d = app.data || {};
  const dirty = status !== app.status || decisionNote !== (app.decision_note || '') || internal !== (app.internal_notes || '');

  return (
    <div className="space-y-3" data-testid={`credit-detail-${app.id}`}>
      <div className="bg-[#0d1219] border border-slate-800 rounded-lg p-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="min-w-0">
            <div className="font-mono text-xs text-emerald-400">{app.reference || 'draft'}</div>
            <div className="text-base font-semibold text-slate-100 truncate">{app.startup_name}</div>
            <div className="text-[11px] text-slate-500 truncate">{app.founder_full_name} · {app.founder_email}</div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button onClick={onExport} disabled={exporting}
              data-testid="credit-detail-export"
              title="Download this application as xlsx"
              className="text-[11px] px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 inline-flex items-center gap-1 disabled:opacity-40">
              <DownloadSimple size={11} /> {exporting ? '…' : 'xlsx'}
            </button>
            <button onClick={onClose} className="text-slate-500 hover:text-slate-300"><X size={14} /></button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-[11px]">
          <KV k="Sought" v={`₹${Number(app.amount_sought_inr || 0).toLocaleString('en-IN')}`} />
          <KV k="Tenure" v={d.tenure_months ? `${d.tenure_months} mo` : '—'} />
          <KV k="Entity" v={d.entity_type || '—'} />
          <KV k="Incorporated" v={d.incorporation_date || '—'} />
          <KV k="Monthly rev" v={`₹${Number(d.monthly_revenue_inr || 0).toLocaleString('en-IN')}`} />
          <KV k="Monthly burn" v={`₹${Number(d.monthly_burn_inr || 0).toLocaleString('en-IN')}`} />
          <KV k="Cash" v={`₹${Number(d.cash_in_bank_inr || 0).toLocaleString('en-IN')}`} />
          <KV k="TTM revenue" v={`₹${Number(d.annual_revenue_inr || 0).toLocaleString('en-IN')}`} />
          <KV k="Existing debt" v={`₹${Number(d.existing_debt_inr || 0).toLocaleString('en-IN')}`} />
          <KV k="Bank" v={d.primary_bank || '—'} />
          <KV k="PAN" v={d.company_pan || '—'} mono />
          <KV k="GSTIN" v={d.gstin || '—'} mono />
        </div>

        {(app.credit_type_names || []).length > 0 && (
          <div className="mt-3 pt-2.5 border-t border-slate-800">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Credit types requested</div>
            <div className="flex flex-wrap gap-1">
              {app.credit_type_names.map((n) => (
                <span key={n} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-300">{n}</span>
              ))}
            </div>
          </div>
        )}

        {d.purpose && (
          <div className="mt-3 pt-2.5 border-t border-slate-800">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Purpose</div>
            <p className="text-[11.5px] text-slate-300 leading-relaxed whitespace-pre-wrap">{d.purpose}</p>
          </div>
        )}
        {d.collateral_notes && (
          <div className="mt-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Security offered</div>
            <p className="text-[11.5px] text-slate-300 leading-relaxed whitespace-pre-wrap">{d.collateral_notes}</p>
          </div>
        )}
        {d.registered_address && (
          <div className="mt-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Registered address</div>
            <p className="text-[11.5px] text-slate-300 leading-relaxed whitespace-pre-wrap">{d.registered_address}</p>
          </div>
        )}

        {/* MoM + documents: the two gates the founder had to clear. Shown so a
            reviewer can see the evidence base without leaving the panel. */}
        <div className="mt-3 pt-2.5 border-t border-slate-800 grid grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">MoM data</div>
            <div className={`text-[11.5px] ${app.mom?.ok ? 'text-emerald-400' : 'text-amber-400'}`}>
              {(app.mom?.present || []).length}/{app.mom?.required || 6} months on file
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Documents</div>
            <div className="text-[11.5px] text-slate-300">
              {(app.documents || []).filter((x) => x.filled).length}/{(app.documents || []).length} uploaded
            </div>
          </div>
        </div>
        <div className="mt-2 space-y-1">
          {(app.documents || []).map((doc) => (
            <div key={`${doc.dataroom_id}::${doc.slot_id}`} className="flex items-center gap-1.5 text-[11px]">
              {doc.filled
                ? <Check size={10} className="text-emerald-400 flex-shrink-0" />
                : <X size={10} className={doc.required ? 'text-amber-400 flex-shrink-0' : 'text-slate-600 flex-shrink-0'} />}
              <span className={doc.filled ? 'text-slate-300' : 'text-slate-500'}>{doc.slot_name}</span>
              {doc.required && !doc.filled && <span className="text-[9px] text-amber-400">required</span>}
              {doc.filled && doc.files?.[0] && (
                <span className="text-slate-500 truncate inline-flex items-center gap-1">
                  <FileText size={9} /> {doc.files[0].filename}
                </span>
              )}
            </div>
          ))}
        </div>

        {app.consent_accepted_at && (
          <div className="mt-3 pt-2.5 border-t border-slate-800 text-[10.5px] text-slate-500">
            Consent {app.consent_version} given {app.consent_accepted_at.slice(0, 10)} · hash {String(app.consent_hash || '').slice(0, 12)}
          </div>
        )}
      </div>

      {/* Pipeline controls */}
      <div className="bg-[#0d1219] border border-slate-800 rounded-lg p-4 space-y-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">Move this application</div>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
          data-testid="credit-detail-status"
          className="w-full bg-slate-900 border border-slate-700 rounded px-2.5 py-2 text-xs text-slate-100">
          {['submitted', 'under_review', 'info_requested', 'approved', 'declined', 'closed'].map((s) => (
            <option key={s} value={s}>{s.replace('_', ' ')}</option>
          ))}
        </select>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">
            Note to the founder <span className="text-slate-600 normal-case">(they see this)</span>
          </label>
          <textarea rows={3} value={decisionNote} onChange={(e) => setDecisionNote(e.target.value)}
            data-testid="credit-detail-decision-note"
            placeholder="Why this decision, or what you need from them."
            className="w-full bg-slate-900 border border-slate-700 rounded px-2.5 py-2 text-xs text-slate-100" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">
            Internal notes <span className="text-slate-600 normal-case">(never shown to the founder)</span>
          </label>
          <textarea rows={2} value={internal} onChange={(e) => setInternal(e.target.value)}
            data-testid="credit-detail-internal-notes"
            placeholder="Which lenders you've approached, indicative terms, concerns."
            className="w-full bg-slate-900 border border-slate-700 rounded px-2.5 py-2 text-xs text-slate-100" />
        </div>
        <button onClick={save} disabled={busy || !dirty}
          data-testid="credit-detail-save"
          className="w-full text-xs font-semibold px-3 py-2 rounded bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-40">
          {busy ? 'Saving…' : dirty ? 'Save changes' : 'No changes'}
        </button>
        <p className="text-[10px] text-slate-500">
          Moving to approved, declined or info requested emails the founder with your note.
        </p>
      </div>

      {/* The founder thread — same component the founder sees. */}
      <div className="bg-[#0d1219] border border-slate-800 rounded-lg p-4">
        {app.chat?.open ? (
          <ServiceChat
            key={app.id}
            requestId={app.id}
            basePath="/credit/applications"
            accent="#10b981"
            counterpartyFallback={app.startup_name}
            placeholder={`Reply to ${app.startup_name}…`}
            emptyHint={<>Nothing yet. Anything you send here reaches the founder as
              &ldquo;Founderboard Credit&rdquo; — never as your own name.</>}
            onRead={onChatRead}
            compact
          />
        ) : (
          <div className="text-[11px] text-slate-500">This application hasn&rsquo;t been submitted yet.</div>
        )}
      </div>
    </div>
  );
};

const KV = ({ k, v, mono }) => (
  <div>
    <div className="text-[9.5px] uppercase tracking-wider text-slate-500">{k}</div>
    <div className={`text-[11.5px] text-slate-200 ${mono ? 'font-mono' : ''}`}>{v}</div>
  </div>
);

const EmailRemindersPage = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/admin/email-reminders');
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load email reminder settings');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (key, enabled) => {
    setBusy(key);
    try {
      const r = await api.patch('/admin/email-reminders/settings', { [key]: enabled });
      setData((prev) => ({ ...prev, settings: r.data.settings }));
      toast.success(enabled ? 'Reminder enabled' : 'Reminder paused');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update reminder');
    } finally { setBusy(''); }
  };

  const run = async (kind, dryRun) => {
    const label = kind === 'startups' ? 'startup reminders' : 'VC weekly digests';
    if (!dryRun && !window.confirm(`Send ${label} now to every currently eligible recipient?`)) return;
    setBusy(`${kind}-${dryRun ? 'preview' : 'send'}`);
    try {
      const r = await api.post(`/admin/email-reminders/${kind}/run?dry_run=${dryRun ? 'true' : 'false'}`);
      if (dryRun) {
        setPreview({ kind, ...r.data });
        toast.success(`Preview ready: ${r.data.recipients || 0} recipient${r.data.recipients === 1 ? '' : 's'}`);
      } else {
        toast.success(`Sent ${r.data.sent || 0} email${r.data.sent === 1 ? '' : 's'}`);
        setPreview(null);
        await load();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to run ${label}`);
    } finally { setBusy(''); }
  };

  if (!data) return <Page title="Email Reminders" testid="email-reminders-page"><div className="text-sm text-slate-500">Loading…</div></Page>;

  const settings = data.settings || {};
  const startup = data.startup || {};
  const vc = data.vc || {};

  return (
    <Page
      title="Email Reminders"
      subtitle="Automated monthly-update nudges for founders and a weekly portfolio status digest for VCs. Scheduling uses India Standard Time."
      testid="email-reminders-page"
      actions={
        <button onClick={load} className="inline-flex items-center gap-1 border border-slate-700 hover:border-slate-500 text-xs px-3 py-1.5 rounded-lg">
          <ArrowClockwise size={11} weight="bold" /> Refresh
        </button>
      }
    >
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <div className="bg-[#111827] border border-slate-800 rounded-xl p-5" data-testid="startup-reminder-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-emerald-400 font-bold">Startups</div>
              <h2 className="text-lg font-heading font-bold mt-1">Pending monthly update</h2>
              <p className="text-sm text-slate-400 mt-1 max-w-xl">Emails startup owners who have not submitted an update for the current month.</p>
            </div>
            <button
              onClick={() => toggle('startup_enabled', !settings.startup_enabled)}
              disabled={busy === 'startup_enabled'}
              className={`text-xs font-semibold px-3 py-2 rounded-lg ${settings.startup_enabled ? 'bg-emerald-500/15 text-emerald-300' : 'bg-slate-800 text-slate-400'}`}
              data-testid="startup-reminder-toggle"
            >{settings.startup_enabled ? 'Enabled' : 'Paused'}</button>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-5">
            <StatCard label="Schedule" value="1 · 15 · Last" sub="10:00 AM IST" tone="slate" />
            <StatCard label="Pending now" value={startup.pending_startups ?? 0} sub={`${startup.recipients ?? 0} email recipients`} tone={(startup.pending_startups || 0) ? 'amber' : 'emerald'} />
          </div>
          <div className="mt-4 text-xs text-slate-500">A startup drops out of the reminder list as soon as a monthly update exists for the current month.</div>
          <div className="flex gap-2 mt-5">
            <button onClick={() => run('startups', true)} disabled={!!busy} className="border border-slate-700 hover:border-slate-500 text-xs px-3 py-2 rounded-lg disabled:opacity-50">Preview recipients</button>
            <button onClick={() => run('startups', false)} disabled={!!busy} className="bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-semibold px-3 py-2 rounded-lg disabled:opacity-50">Send now</button>
          </div>
        </div>

        <div className="bg-[#111827] border border-slate-800 rounded-xl p-5" data-testid="vc-reminder-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-violet-400 font-bold">VCs</div>
              <h2 className="text-lg font-heading font-bold mt-1">Weekly portfolio digest</h2>
              <p className="text-sm text-slate-400 mt-1 max-w-xl">One digest per fund listing recent submitters and portfolio companies still pending this month.</p>
            </div>
            <button
              onClick={() => toggle('vc_enabled', !settings.vc_enabled)}
              disabled={busy === 'vc_enabled'}
              className={`text-xs font-semibold px-3 py-2 rounded-lg ${settings.vc_enabled ? 'bg-emerald-500/15 text-emerald-300' : 'bg-slate-800 text-slate-400'}`}
              data-testid="vc-reminder-toggle"
            >{settings.vc_enabled ? 'Enabled' : 'Paused'}</button>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-5">
            <StatCard label="Schedule" value="Monday" sub="10:00 AM IST" tone="slate" />
            <StatCard label="Funds now" value={vc.funds ?? 0} sub={`${vc.recipients ?? 0} team recipients`} tone="slate" />
          </div>
          <div className="mt-4 text-xs text-slate-500">“Completed” means submitted in the last 7 days. “Pending” means no current-month update exists for that portfolio startup.</div>
          <div className="flex gap-2 mt-5">
            <button onClick={() => run('vcs', true)} disabled={!!busy} className="border border-slate-700 hover:border-slate-500 text-xs px-3 py-2 rounded-lg disabled:opacity-50">Preview digests</button>
            <button onClick={() => run('vcs', false)} disabled={!!busy} className="bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-semibold px-3 py-2 rounded-lg disabled:opacity-50">Send now</button>
          </div>
        </div>
      </div>

      {preview && (
        <div className="mt-5 bg-[#111827] border border-slate-800 rounded-xl p-5" data-testid="email-reminder-preview">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Preview</div>
              <div className="text-sm font-semibold mt-0.5">{preview.kind === 'startups' ? `${preview.pending_startups || 0} pending startups` : `${preview.funds || 0} fund digests`}</div>
            </div>
            <button onClick={() => setPreview(null)} className="text-xs text-slate-500 hover:text-slate-200">Close</button>
          </div>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {(preview.preview || []).map((row, i) => preview.kind === 'startups' ? (
              <div key={`${row.email}-${i}`} className="flex justify-between gap-4 border-t border-slate-800 pt-2 text-xs">
                <span className="text-slate-200">{row.startup_name}</span><span className="text-slate-500 font-mono">{row.email}</span>
              </div>
            ) : (
              <div key={`${row.fund_name}-${i}`} className="border-t border-slate-800 pt-2 text-xs flex items-center justify-between gap-4">
                <div><span className="text-slate-200 font-semibold">{row.fund_name}</span><div className="text-slate-500 mt-0.5">{(row.recipients || []).join(', ')}</div></div>
                <div className="text-right text-slate-400"><div>{row.completed_count} updated</div><div>{row.pending_count} pending</div></div>
              </div>
            ))}
            {!(preview.preview || []).length && <div className="text-sm text-slate-500">No eligible recipients right now.</div>}
          </div>
        </div>
      )}

      <div className="mt-5 bg-[#111827] border border-slate-800 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-800 text-xs uppercase tracking-wider font-bold text-slate-500">Recent reminder deliveries</div>
        <div className="divide-y divide-slate-800">
          {(data.recent_deliveries || []).map((row, i) => (
            <div key={`${row.recipient_email}-${i}`} className="px-5 py-3 flex items-center justify-between gap-4 text-xs">
              <div><span className="text-slate-200">{row.recipient_email}</span><span className="text-slate-600 ml-2">{row.campaign === 'vc_weekly' ? 'VC weekly' : 'Startup pending'}</span></div>
              <div className="text-right"><span className="text-emerald-400 uppercase text-[10px] font-bold">{row.status}</span><div className="text-slate-600 mt-0.5">{row.created_at?.slice(0, 16).replace('T', ' ')}</div></div>
            </div>
          ))}
          {!(data.recent_deliveries || []).length && <div className="px-5 py-4 text-sm text-slate-500">No reminder emails have been sent yet.</div>}
        </div>
      </div>
    </Page>
  );
};

const AuditPage = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get('/admin/audit-log').then((r) => setRows(r.data.entries || [])); }, []);
  return (
    <Page title="Audit Log" subtitle="Every sensitive admin action." testid="audit-page">
      <Table testid="audit-table" cols={[
        { key: 'created_at', label: 'When', render: (r) => <span className="text-xs text-slate-400 font-mono">{r.created_at?.slice(0, 19).replace('T', ' ')}</span> },
        { key: 'action', label: 'Action', render: (r) => <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400">{r.action}</span> },
        { key: 'actor_email', label: 'Actor', render: (r) => <span className="text-xs font-mono">{r.actor_email || r.actor_id?.slice(0, 8)}</span> },
        { key: 'target_email', label: 'Target', render: (r) => <span className="text-xs">{r.target_email || r.target_user_id?.slice(0, 8) || '—'}</span> },
        { key: 'workspace_id', label: 'Scope', render: (r) => <span className="text-[10px] text-slate-500">{r.workspace_id}</span> },
      ]} rows={rows} empty="No audit entries." />
    </Page>
  );
};

// ─────────────── MODULE ACCESS ───────────────
// Page-level open/close switches for Services, Founder Credit, Marketing and
// Studios. Closing one leaves the nav tab in place and swaps the page body for
// the "coming soon" banner whose copy is edited right here.

const AUDIENCE_LABEL = {
  startup: 'Startup app',
  vc: 'VC & Incubator app',
};

const DEFAULT_BANNER_MESSAGE = "We're putting the finishing touches on this feature. Stay tuned!";

const ModuleAccessPage = () => {
  const [modules, setModules] = useState(null);

  const load = async () => {
    try {
      const r = await api.get('/admin/module-access');
      setModules(r.data.modules || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load modules'); }
  };
  useEffect(() => { load(); }, []);

  const onSaved = (updated) =>
    setModules((prev) => prev.map((m) => (m.key === updated.key ? updated : m)));

  if (!modules) return <Page title="Module Access" testid="modules-page"><div className="text-sm text-slate-500">Loading…</div></Page>;

  const openCount = modules.filter((m) => m.enabled).length;
  const groups = ['startup', 'vc'].filter((a) => modules.some((m) => m.audience === a));

  return (
    <Page
      title="Module Access"
      testid="modules-page"
      subtitle={`${openCount} of ${modules.length} modules open. Closing one keeps its nav tab but replaces the page with a "coming soon" banner — users keep their links, nothing is deleted.`}
      actions={
        <button onClick={load} data-testid="modules-refresh"
          className="inline-flex items-center gap-1 border border-slate-700 hover:border-slate-500 text-xs px-3 py-1.5 rounded-lg">
          <ArrowClockwise size={11} weight="bold" /> Refresh
        </button>
      }>
      <div className="space-y-6">
        {groups.map((audience) => (
          <div key={audience}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">
              {AUDIENCE_LABEL[audience] || audience}
            </div>
            <div className="space-y-3">
              {modules.filter((m) => m.audience === audience).map((m) => (
                <ModuleCard key={m.key} module={m} onSaved={onSaved} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </Page>
  );
};

const ModuleCard = ({ module: m, onSaved }) => {
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState(m.banner_title || '');
  const [message, setMessage] = useState(m.banner_message || '');

  // A refresh or another admin's toggle should win over stale local edits.
  useEffect(() => { setTitle(m.banner_title || ''); setMessage(m.banner_message || ''); }, [m.banner_title, m.banner_message]);

  const bannerDirty = title !== (m.banner_title || '') || message !== (m.banner_message || '');

  const patch = async (body, successMsg) => {
    setBusy(true);
    try {
      const r = await api.patch(`/admin/module-access/${m.key}`, body);
      onSaved(r.data.module);
      toast.success(successMsg);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally { setBusy(false); }
  };

  const toggle = () => {
    const next = !m.enabled;
    if (!window.confirm(next
      ? `Open "${m.label}" for everyone?`
      : `Close "${m.label}"? Users will see the coming-soon banner instead of the page.`)) return;
    patch({ enabled: next }, next ? `${m.label} is now open` : `${m.label} is now closed`);
  };

  return (
    <div className={`bg-[#111827] border rounded-xl p-4 ${m.enabled ? 'border-slate-800' : 'border-amber-500/30'}`}
      data-testid={`module-card-${m.key}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-100">{m.label}</span>
            <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-bold ${m.enabled ? 'bg-emerald-500/15 text-emerald-400' : 'bg-amber-500/15 text-amber-400'}`}
              data-testid={`module-status-${m.key}`}>
              {m.enabled ? 'Open' : 'Closed'}
            </span>
          </div>
          <div className="text-xs text-slate-400 mt-1">{m.summary}</div>
          <div className="text-[10px] font-mono text-slate-600 mt-1">{m.key} · {m.path}</div>
          {m.updated_at && (
            <div className="text-[10px] text-slate-600 mt-1">
              Last changed {m.updated_at.slice(0, 16).replace('T', ' ')}{m.updated_by_email ? ` by ${m.updated_by_email}` : ''}
            </div>
          )}
        </div>
        <button onClick={toggle} disabled={busy} data-testid={`module-toggle-${m.key}`}
          className={`inline-flex items-center gap-1.5 shrink-0 text-xs font-semibold px-3 py-2 rounded-lg disabled:opacity-40 ${
            m.enabled
              ? 'bg-amber-500/15 text-amber-300 hover:bg-amber-500/25'
              : 'bg-emerald-500 hover:bg-emerald-400 text-black'}`}>
          {m.enabled
            ? <><LockSimple size={11} weight="bold" /> Close page</>
            : <><LockSimpleOpen size={11} weight="bold" /> Open page</>}
        </button>
      </div>

      <div className="mt-4 pt-3 border-t border-slate-800">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">
          Banner shown while closed
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-2">
          <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80}
            placeholder={m.label} data-testid={`module-banner-title-${m.key}`}
            className="bg-[#0a0e14] border border-slate-800 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" />
          <input value={message} onChange={(e) => setMessage(e.target.value)} maxLength={400}
            placeholder={DEFAULT_BANNER_MESSAGE} data-testid={`module-banner-message-${m.key}`}
            className="bg-[#0a0e14] border border-slate-800 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" />
        </div>
        <div className="flex items-center gap-2 mt-2">
          <button onClick={() => patch({ banner_title: title, banner_message: message }, 'Banner updated')}
            disabled={busy || !bannerDirty} data-testid={`module-banner-save-${m.key}`}
            className="text-xs px-3 py-1.5 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold rounded-lg disabled:opacity-40">
            {busy ? 'Saving…' : 'Save banner'}
          </button>
          <button onClick={() => { setTitle(m.banner_title || ''); setMessage(m.banner_message || ''); }}
            disabled={!bannerDirty} data-testid={`module-banner-reset-${m.key}`}
            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 border border-slate-700 hover:border-slate-500 rounded-lg disabled:opacity-30">
            <ArrowCounterClockwise size={10} weight="bold" /> Revert
          </button>
          <span className="text-[10px] text-slate-600">Leave a field empty to fall back to the default copy.</span>
        </div>
      </div>
    </div>
  );
};

const FlagsPage = () => {
  const [flags, setFlags] = useState([]); const [form, setForm] = useState({ key: '', description: '', enabled: false });
  const load = async () => { const r = await api.get('/admin/feature-flags'); setFlags(r.data.flags || []); };
  useEffect(() => { load(); }, []);
  const save = async (e) => { e.preventDefault(); try { await api.post('/admin/feature-flags', form); setForm({ key: '', description: '', enabled: false }); load(); } catch (err) { toast.error('Failed'); } };
  const toggle = async (f) => { await api.post('/admin/feature-flags', { ...f, enabled: !f.enabled }); load(); };
  const del = async (f) => { if (!window.confirm(`Delete ${f.key}?`)) return; await api.delete(`/admin/feature-flags/${f.key}`); load(); };
  return (
    <Page title="Feature Flags" subtitle="Toggle features without redeploying." testid="flags-page">
      <form onSubmit={save} className="bg-[#111827] border border-slate-800 rounded-xl p-4 mb-4 flex gap-2 flex-wrap" data-testid="flag-form">
        <input required placeholder="flag_key" value={form.key} onChange={(e) => setForm((p) => ({ ...p, key: e.target.value }))} data-testid="flag-key" className="bg-[#0a0e14] border border-slate-800 rounded-lg px-3 py-2 text-sm font-mono flex-1 min-w-[180px]" />
        <input placeholder="description" value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} data-testid="flag-desc" className="bg-[#0a0e14] border border-slate-800 rounded-lg px-3 py-2 text-sm flex-[2] min-w-[240px]" />
        <label className="inline-flex items-center gap-1 text-xs text-slate-400"><input type="checkbox" checked={form.enabled} onChange={(e) => setForm((p) => ({ ...p, enabled: e.target.checked }))} data-testid="flag-enabled" /> enabled</label>
        <button type="submit" data-testid="flag-save" className="bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-semibold px-3 py-2 rounded-lg">Save flag</button>
      </form>
      <Table testid="flags-table" cols={[
        { key: 'key', label: 'Key', render: (r) => <span className="font-mono text-xs">{r.key}</span> },
        { key: 'description', label: 'Description' },
        { key: 'enabled', label: 'On?', render: (r) => <button onClick={() => toggle(r)} data-testid={`flag-toggle-${r.key}`} className={`text-[10px] px-2 py-0.5 rounded ${r.enabled ? 'bg-emerald-500/15 text-emerald-400' : 'bg-slate-800 text-slate-500'}`}>{r.enabled ? 'on' : 'off'}</button> },
        { key: 'actions', label: '', render: (r) => <button onClick={() => del(r)} data-testid={`flag-del-${r.key}`} className="text-red-400"><TrashSimple size={11} weight="bold" /></button> },
      ]} rows={flags} empty="No flags yet." />
    </Page>
  );
};

const SystemPage = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get('/admin/health').then((r) => setD(r.data)); }, []);
  if (!d) return <Page title="System" testid="system-page"><div className="text-sm text-slate-500">Loading…</div></Page>;
  return (
    <Page title="System Health" subtitle={`Server time: ${d.server_time}`} testid="system-page">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <StatCard label="MongoDB" value={d.mongo.ok ? 'OK' : 'FAIL'} tone={d.mongo.ok ? 'emerald' : 'red'} />
        {Object.entries(d.counts).map(([k, v]) => <StatCard key={k} label={k.replace(/_/g, ' ')} value={v.toLocaleString('en-IN')} tone="slate" />)}
      </div>
    </Page>
  );
};

const AdminsPage = () => {
  const [reqs, setReqs] = useState([]);
  const load = async () => { const r = await api.get('/admin/signup-requests'); setReqs(r.data.requests || []); };
  useEffect(() => { load(); }, []);
  const act = async (r, path) => { try { await api.post(`/admin/signup-requests/${r.id}/${path}`, {}); toast.success('Done'); load(); } catch (e) { toast.error('Failed'); } };
  return (
    <Page title="Admin Access" subtitle="Approve or reject admin signup requests." testid="admins-page">
      <Table testid="admins-table" cols={[
        { key: 'full_name', label: 'Name' },
        { key: 'email', label: 'Email', render: (r) => <span className="font-mono text-xs">{r.email}</span> },
        { key: 'reason', label: 'Reason', render: (r) => <span className="text-xs text-slate-400 line-clamp-1">{r.reason || '—'}</span> },
        { key: 'created_at', label: 'When', render: (r) => <span className="text-xs text-slate-400">{r.created_at?.slice(0, 10)}</span> },
        { key: 'actions', label: '', render: (r) => (
          <div className="flex gap-1">
            <button onClick={() => act(r, 'approve')} data-testid={`admin-approve-${r.id}`} className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400">Approve</button>
            <button onClick={() => act(r, 'reject')} data-testid={`admin-reject-${r.id}`} className="text-[10px] px-2 py-0.5 rounded bg-red-500/15 text-red-400">Reject</button>
          </div>
        )},
      ]} rows={reqs} empty="No pending requests." />
    </Page>
  );
};

// ─────────────── SETTINGS (Change Password) ───────────────
const SettingsPage = ({ user }) => {
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    if (form.new_password !== form.confirm_password) { toast.error('Passwords do not match'); return; }
    setBusy(true);
    try {
      await api.post('/auth/change-password', {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success('Password updated. Please use it on your next login.');
      setForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally { setBusy(false); }
  };
  return (
    <Page title="Settings" subtitle="Manage your admin account." testid="settings-page">
      <div className="max-w-lg bg-[#111827] border border-slate-800 rounded-xl p-6">
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Signed in as</div>
          <div className="text-sm font-mono">{user?.email}</div>
          <div className="text-[10px] text-emerald-400 mt-0.5">{user?.is_super_admin ? 'Super admin' : (user?.studio_role || 'admin')}</div>
        </div>
        <div className="border-t border-slate-800 pt-5">
          <h3 className="text-sm font-semibold mb-3 text-slate-200">Change password</h3>
          <form onSubmit={submit} className="space-y-3" data-testid="change-password-form">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">Current password</label>
              <input required type="password" value={form.current_password} onChange={(e) => setForm((p) => ({ ...p, current_password: e.target.value }))} data-testid="current-password" className="w-full bg-[#0a0e14] border border-slate-800 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">New password <span className="text-slate-600">(min 8 chars)</span></label>
              <input required type="password" value={form.new_password} onChange={(e) => setForm((p) => ({ ...p, new_password: e.target.value }))} data-testid="new-password" className="w-full bg-[#0a0e14] border border-slate-800 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">Confirm new password</label>
              <input required type="password" value={form.confirm_password} onChange={(e) => setForm((p) => ({ ...p, confirm_password: e.target.value }))} data-testid="confirm-password" className="w-full bg-[#0a0e14] border border-slate-800 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" />
            </div>
            <button disabled={busy} type="submit" data-testid="change-password-submit" className="bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-50">{busy ? 'Updating…' : 'Update password'}</button>
          </form>
        </div>
      </div>
    </Page>
  );
};

export default AdminApp;
